import { useState, useEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Settings,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  Database,
  RefreshCw,
  ArrowUpDown,
  Menu,
  FileText,
  ExternalLink,
  X,
  Pencil,
  Save,
  Plus,
  Check,
  Eye,
  Image,
  File,
  Download,
  Upload,
  ZoomIn,
  Filter,
  ChevronDown,
  ChevronUp,
  LogOut
} from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function PortalView({ 
  isPublicView = false, 
  publicPortalData = null,
  publicShareToken = null,
  publicUserToken = null,
  portalUser = null,
  onLogout = null,
  onReady = null  // Callback when portal is ready (records loaded)
}) {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const { token, csrfToken } = useAuth();
  
  // Use public data if in public view mode
  const [portal, setPortal] = useState(publicPortalData);
  const [pages, setPages] = useState(publicPortalData?.pages || []);
  const [activePage, setActivePage] = useState(publicPortalData?.pages?.[0] || null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(!isPublicView); // Don't show loading if we already have public data
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [offset, setOffset] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Record detail dialog
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  
  // Edit mode - now per-field inline editing
  const [editingFieldName, setEditingFieldName] = useState(null); // Which field is being edited
  const [editedFieldValue, setEditedFieldValue] = useState(null); // Value of the field being edited
  const [isSaving, setIsSaving] = useState(false);
  
  // Legacy edit mode state (keep for compatibility)
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  
  // State to track which linked record fields are expanded (show all vs show 4)
  const [expandedLinkedFields, setExpandedLinkedFields] = useState({});
  
  // Create new record
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRecordFields, setNewRecordFields] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [tableSchema, setTableSchema] = useState(null);
  const [fullSchema, setFullSchema] = useState(null);
  
  // Linked records cache - stores records from linked tables
  const [linkedRecordsCache, setLinkedRecordsCache] = useState({});
  const [loadingLinkedRecords, setLoadingLinkedRecords] = useState({});
  const [linkedRecordSearch, setLinkedRecordSearch] = useState({});
  
  // Linked record preview
  const [previewRecord, setPreviewRecord] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Create new linked record
  const [createLinkedDialogOpen, setCreateLinkedDialogOpen] = useState(false);
  const [createLinkedFieldName, setCreateLinkedFieldName] = useState(null);
  const [createLinkedTableId, setCreateLinkedTableId] = useState(null);
  const [newLinkedRecordFields, setNewLinkedRecordFields] = useState({});
  const [isCreatingLinked, setIsCreatingLinked] = useState(false);
  
  // State for searching linked records within the Create form
  const [createFormLinkedSearch, setCreateFormLinkedSearch] = useState({}); // { fieldName: searchTerm }
  const [createFormLinkedCache, setCreateFormLinkedCache] = useState({}); // { tableId: records[] }
  const [createFormLinkedLoading, setCreateFormLinkedLoading] = useState({}); // { tableId: boolean }

  // Expanded linked record view (for viewing mode, not editing)
  const [expandedLinkedRecord, setExpandedLinkedRecord] = useState(null);
  const [expandedLinkedDialogOpen, setExpandedLinkedDialogOpen] = useState(false);
  const [loadingExpandedRecord, setLoadingExpandedRecord] = useState(false);
  const [expandedLinkedParentField, setExpandedLinkedParentField] = useState(null); // Track which parent field triggered the expand
  
  // Linked records for view mode (to show meaningful content instead of IDs)
  const [viewLinkedRecordsCache, setViewLinkedRecordsCache] = useState({});
  const [loadingViewLinkedRecords, setLoadingViewLinkedRecords] = useState({});
  
  // Ref to always access the latest cache state (fixes stale closure issues)
  const viewLinkedRecordsCacheRef = useRef(viewLinkedRecordsCache);
  const prefetchedTablesRef = useRef(new Set()); // Track which tables have been prefetched

  // File/Attachment preview
  const [previewFileDialogOpen, setPreviewFileDialogOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  
  // Pending file uploads for create form
  const [createFormPendingFiles, setCreateFormPendingFiles] = useState({}); // { fieldName: [{url, filename}] };
  
  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadFieldName, setUploadFieldName] = useState(null);

  // Quick Filter Chips state
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [activeFilters, setActiveFilters] = useState({}); // { fieldName: [selectedValues] }
  const [loadingAllRecords, setLoadingAllRecords] = useState(false);
  const [allRecordsLoaded, setAllRecordsLoaded] = useState(false);

  // Get branding colors from portal
  const primaryColor = portal?.branding?.primary_color || "#059669";

  // Auth headers - use public token for public view, admin token otherwise
  // Memoized to prevent infinite re-renders
  const authHeaders = useMemo(() => isPublicView 
    ? { headers: publicUserToken ? { 'X-Portal-User-Token': publicUserToken } : {} }
    : { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    [isPublicView, publicUserToken, token]
  );
  
  // Headers including CSRF token for state-changing requests (POST, PATCH, DELETE)
  const authHeadersWithCsrf = useMemo(() => isPublicView 
    ? { headers: publicUserToken ? { 'X-Portal-User-Token': publicUserToken } : {} }
    : { 
        headers: { 
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
        }
      },
    [isPublicView, publicUserToken, token, csrfToken]
  );

  // Keep the ref in sync with the state (to fix stale closures)
  useEffect(() => {
    viewLinkedRecordsCacheRef.current = viewLinkedRecordsCache;
  }, [viewLinkedRecordsCache]);

  // Initialize from public data if in public view
  useEffect(() => {
    if (isPublicView && publicPortalData) {
      setPortal(publicPortalData);
      setPages(publicPortalData.pages || []);
      if (publicPortalData.pages?.length > 0 && !activePage) {
        setActivePage(publicPortalData.pages[0]);
      }
      setLoading(false);
    }
  }, [isPublicView, publicPortalData]);

  useEffect(() => {
    if (!isPublicView) {
      fetchPortal();
    }
  }, [portalId, isPublicView]);

  useEffect(() => {
    if (activePage) {
      fetchRecords();
      // Reset all filter/loading states when changing pages
      setActiveFilters({});
      setFiltersExpanded(false);
      setAllRecordsLoaded(false);
      setLoadingAllRecords(false);
      // Clear prefetch cache for new page
      prefetchedTablesRef.current.clear();
    }
  }, [activePage]);

  // Ref to get current records and offset without causing re-renders
  const recordsRef = useRef(records);
  const offsetRef = useRef(offset);
  const loadingAllRecordsRef = useRef(loadingAllRecords);
  
  useEffect(() => {
    recordsRef.current = records;
    offsetRef.current = offset;
    loadingAllRecordsRef.current = loadingAllRecords;
  }, [records, offset, loadingAllRecords]);

  // Auto-load ALL records when filters are expanded
  useEffect(() => {
    // Don't do anything if filters aren't expanded
    if (!filtersExpanded) return;
    
    // Don't do anything if already loaded all
    if (allRecordsLoaded) return;
    
    // Don't do anything if currently loading (check ref to avoid self-cancellation)
    if (loadingAllRecordsRef.current) return;
    
    // Don't do anything if there's nothing more to load
    if (!hasMore) {
      setAllRecordsLoaded(true);
      return;
    }
    
    // Don't start if we don't have an active page or offset
    if (!activePage || !offsetRef.current) return;
    
    let isCancelled = false;
    
    const loadAll = async () => {
      setLoadingAllRecords(true);
      
      try {
        // Use refs to get current values without triggering re-render
        let allRecords = [...recordsRef.current];
        let nextOffset = offsetRef.current;
        const formulaToSend = isPublicView ? undefined : (activePage?.filter_formula || undefined);
        
        while (nextOffset && !isCancelled) {
          const requestBody = {
            table_name: activePage.table_name,
            page_size: 100,
            offset: nextOffset,
            fields: activePage.visible_fields,
            formula: formulaToSend,
            view: activePage.airtable_view
          };

          let response;
          if (isPublicView) {
            response = await axios.post(
              `${API}/portal/${publicShareToken}/authenticated/page/${activePage.page_id}/records`,
              requestBody,
              authHeaders
            );
          } else {
            response = await axios.post(
              `${API}/airtable/records?portal_id=${portalId}`,
              requestBody,
              authHeaders
            );
          }
          
          const newRecords = response.data.records || [];
          allRecords = [...allRecords, ...newRecords];
          nextOffset = response.data.offset || null;
        }
        
        if (!isCancelled) {
          setRecords(allRecords);
          setOffset(null);
          setHasMore(false);
          setAllRecordsLoaded(true);
        }
      } catch (error) {
        console.error('[loadAll] Error:', error?.response?.data || error.message);
        // Mark as done to prevent infinite loop
        if (!isCancelled) {
          setAllRecordsLoaded(true);
        }
      } finally {
        if (!isCancelled) {
          setLoadingAllRecords(false);
        }
      }
    };
    
    loadAll();
    
    return () => { isCancelled = true; };
    // IMPORTANT: Removed loadingAllRecords from deps to prevent self-cancellation
  }, [filtersExpanded, hasMore, allRecordsLoaded, activePage, isPublicView, publicShareToken, portalId, authHeaders]);

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Trigger search when debounced search term changes
  useEffect(() => {
    if (activePage) {
      fetchRecords(null, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  // Pre-fetch linked record data for table view display
  useEffect(() => {
    const prefetchLinkedRecordsForTable = async () => {
      if (!records || records.length === 0 || !fullSchema || !tableSchema) return;
      
      // Skip if already prefetched for this table
      const tableKey = `${activePage?.table_name}_${records.length}`;
      if (prefetchedTablesRef.current.has(tableKey)) return;
      prefetchedTablesRef.current.add(tableKey);
      
      // Find all multipleRecordLinks fields in the current table
      const linkedFields = (tableSchema.fields || []).filter(
        f => f.type === 'multipleRecordLinks' && f.options?.linkedTableId
      );
      
      if (linkedFields.length === 0) return;
      
      // Process all linked fields in parallel for better performance
      const fetchPromises = linkedFields.map(async (field) => {
        const linkedTableId = field.options.linkedTableId;
        const allRecordIds = new Set();
        
        for (const record of records) {
          const fieldValue = record.fields?.[field.name];
          if (Array.isArray(fieldValue)) {
            fieldValue.forEach(id => {
              if (typeof id === 'string' && id.startsWith('rec')) {
                allRecordIds.add(id);
              }
            });
          }
        }
        
        if (allRecordIds.size > 0) {
          // Fetch all unique IDs (up to 100 per batch request)
          const idsArray = Array.from(allRecordIds);
          // Split into batches of 100 to avoid overwhelming the API
          for (let i = 0; i < idsArray.length; i += 100) {
            const batch = idsArray.slice(i, i + 100);
            await fetchViewLinkedRecords(linkedTableId, batch);
          }
        }
      });
      
      await Promise.all(fetchPromises);
    };
    
    prefetchLinkedRecordsForTable();
  }, [records, fullSchema, tableSchema, activePage?.table_name]);

  // Fetch linked records when a record dialog opens (for view mode)
  useEffect(() => {
    const fetchLinkedRecordsForView = async () => {
      if (!recordDialogOpen || !selectedRecord || !fullSchema) return;
      
      const fields = selectedRecord.fields || {};
      const tableFields = tableSchema?.fields || [];
      
      for (const fieldDef of tableFields) {
        if (fieldDef.type === 'multipleRecordLinks') {
          const linkedTableId = fieldDef.options?.linkedTableId;
          const fieldValue = fields[fieldDef.name];
          
          if (!linkedTableId || !fieldValue) continue;
          
          // Parse the value (could be array or comma-separated string)
          let recordIds = [];
          if (Array.isArray(fieldValue)) {
            // Filter to only include valid record IDs
            recordIds = fieldValue.filter(id => typeof id === 'string' && id.startsWith('rec'));
          } else if (typeof fieldValue === 'string' && fieldValue.trim()) {
            recordIds = fieldValue.split(',').map(v => v.trim()).filter(v => v.startsWith('rec'));
          }
          
          if (recordIds.length > 0) {
            // Check if any are not cached - use ref to get latest state
            const currentCache = viewLinkedRecordsCacheRef.current;
            const uncachedIds = recordIds.filter(id => !currentCache[id]);
            if (uncachedIds.length > 0) {
              await fetchViewLinkedRecords(linkedTableId, uncachedIds);
            }
          }
        }
      }
    };
    
    fetchLinkedRecordsForView();
  }, [recordDialogOpen, selectedRecord, fullSchema, tableSchema]);

  const fetchPortal = async () => {
    try {
      const response = await axios.get(`${API}/portals/${portalId}`, authHeaders);
      setPortal(response.data);
      setPages(response.data.pages || []);
      if (response.data.pages?.length > 0) {
        setActivePage(response.data.pages[0]);
      }
    } catch (error) {
      toast.error("Failed to load portal");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  // Unified API helper - handles both admin and public view endpoints
  const fetchPageRecords = async (pageSize, offsetValue, formula) => {
    const requestBody = {
      table_name: activePage.table_name,
      page_size: pageSize,
      offset: offsetValue,
      fields: activePage.visible_fields,
      formula: formula || undefined,
      view: activePage.airtable_view
    };

    if (isPublicView) {
      return axios.post(
        `${API}/portal/${publicShareToken}/authenticated/page/${activePage.page_id}/records`,
        requestBody,
        authHeaders
      );
    } else {
      return axios.post(
        `${API}/airtable/records?portal_id=${portalId}`,
        requestBody,
        authHeaders
      );
    }
  };

  const fetchRecords = async (newOffset = null, searchQuery = null) => {
    if (!activePage) return;
    setRecordsLoading(true);
    if (searchQuery !== null) {
      setIsSearching(true);
    }
    
    // Fetch schema if not already loaded (needed for filters)
    if (!tableSchema) {
      try {
        // Use different endpoint for public view
        let schemaResponse;
        if (isPublicView) {
          schemaResponse = await axios.get(
            `${API}/portal/${publicShareToken}/authenticated/schema`,
            authHeaders
          );
        } else {
          schemaResponse = await axios.get(`${API}/airtable/schema?portal_id=${portalId}`, authHeaders);
        }
        setFullSchema(schemaResponse.data);
        const table = schemaResponse.data.tables?.find(t => t.name === activePage?.table_name);
        setTableSchema(table);
      } catch (error) {
        console.error("Failed to fetch schema:", error);
      }
    }
    
    try {
      // Build filter formula
      // For PUBLIC view: Only send additional filters (like search) - backend already applies page.filter_formula
      // For ADMIN view: Send full formula including page.filter_formula
      let formula = isPublicView ? '' : (activePage.filter_formula || '');
      
      // If there's a search query, search by "Full Name" field only
      if (searchQuery && searchQuery.trim()) {
        const searchFormula = `SEARCH(LOWER("${searchQuery.trim()}"), LOWER({Full Name}))`;
        // Combine with existing filter if present
        if (formula) {
          formula = `AND(${formula}, ${searchFormula})`;
        } else {
          formula = searchFormula;
        }
      }
      
      // Use unified API helper
      const response = await fetchPageRecords(
        searchQuery ? 100 : 50,
        newOffset,
        formula
      );
      
      const newRecords = response.data.records || [];
      setRecords(newOffset ? [...records, ...newRecords] : newRecords);
      setOffset(response.data.offset || null);
      setHasMore(!!response.data.offset);
      
      // Call onReady callback when initial records are loaded (not pagination)
      if (!newOffset && onReady) {
        onReady();
      }
    } catch (error) {
      toast.error("Failed to load records");
      // Also call onReady on error to hide loading overlay
      if (onReady) {
        onReady();
      }
    } finally {
      setRecordsLoading(false);
      setIsSearching(false);
    }
  };

  const handlePageChange = (page) => {
    setActivePage(page);
    setRecords([]);
    setOffset(null);
    setSearchTerm("");
    setSortField(null);
    setAllRecordsLoaded(false);
    setLoadingAllRecords(false);  // Reset loading state
    setFiltersExpanded(false);    // Collapse filters
    setActiveFilters({});         // Clear filters
    // Reset table schema when changing pages - it will be re-fetched for the new table
    setTableSchema(null);
  };

  const handleLoadMore = () => {
    if (offset && !recordsLoading) {
      fetchRecords(offset);
    }
  };

  const handleRefresh = () => {
    setOffset(null);
    setAllRecordsLoaded(false);
    fetchRecords();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const openRecordDetail = async (record) => {
    setSelectedRecord(record);
    setEditedFields({ ...record.fields });
    setIsEditing(false);
    
    // Fetch schema if needed
    let schema = fullSchema;
    if (!schema) {
      await fetchTableSchema();
      schema = fullSchema;
    }
    
    // Pre-fetch linked records BEFORE opening the dialog
    const fields = record.fields || {};
    const tableFields = tableSchema?.fields || [];
    
    for (const fieldDef of tableFields) {
      if (fieldDef.type === 'multipleRecordLinks') {
        const linkedTableId = fieldDef.options?.linkedTableId;
        const fieldValue = fields[fieldDef.name];
        
        if (!linkedTableId || !fieldValue) continue;
        
        let recordIds = [];
        if (Array.isArray(fieldValue)) {
          recordIds = fieldValue.filter(id => typeof id === 'string' && id.startsWith('rec'));
        } else if (typeof fieldValue === 'string' && fieldValue.trim()) {
          recordIds = fieldValue.split(',').map(v => v.trim()).filter(v => v.startsWith('rec'));
        }
        
        if (recordIds.length > 0) {
          await fetchViewLinkedRecords(linkedTableId, recordIds);
        }
      }
    }
    
    // Use flushSync to force synchronous state updates
    // This ensures the modal renders with the latest cache data
    flushSync(() => {
      // Sync the state with the ref which has the latest data
      setViewLinkedRecordsCache(viewLinkedRecordsCacheRef.current);
    });
    
    // Now open the dialog after cache state is synchronized
    setRecordDialogOpen(true);
  };

  const handleCancelEdit = () => {
    setEditedFields({ ...selectedRecord.fields });
    setIsEditing(false);
  };

  const handleFieldChange = (fieldName, value) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSaveRecord = async () => {
    if (!selectedRecord || !activePage) return;
    setIsSaving(true);
    try {
      // Read-only field types that cannot be updated
      const readOnlyTypes = ['autoNumber', 'formula', 'rollup', 'lookup', 'count', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy', 'button'];
      
      // Get only the fields that were actually changed
      const originalFields = selectedRecord.fields || {};
      const changedFields = {};
      
      for (const [fieldName, editedValue] of Object.entries(editedFields)) {
        const originalValue = originalFields[fieldName];
        
        // Check if value actually changed
        let hasChanged = false;
        if (Array.isArray(originalValue) && Array.isArray(editedValue)) {
          hasChanged = JSON.stringify(originalValue) !== JSON.stringify(editedValue);
        } else {
          hasChanged = originalValue !== editedValue;
        }
        
        if (!hasChanged) continue;
        
        // Check if this is a read-only field type
        const fieldSchema = tableSchema?.fields?.find(f => f.name === fieldName);
        if (fieldSchema && readOnlyTypes.includes(fieldSchema.type)) {
          console.log(`Skipping read-only field: ${fieldName} (${fieldSchema.type})`);
          continue;
        }
        
        // For linked records (multipleRecordLinks), ensure we're sending valid record IDs
        if (fieldSchema?.type === 'multipleRecordLinks') {
          if (Array.isArray(editedValue)) {
            // Filter to only include valid record IDs (strings starting with 'rec')
            const validIds = editedValue.filter(id => typeof id === 'string' && id.startsWith('rec'));
            if (validIds.length > 0) {
              changedFields[fieldName] = validIds;
            }
          }
          continue;
        }
        
        // For attachments, skip as they require special handling
        if (fieldSchema?.type === 'multipleAttachments') {
          console.log(`Skipping attachment field: ${fieldName}`);
          continue;
        }
        
        // Add the changed field
        changedFields[fieldName] = editedValue;
      }
      
      // If no fields changed, show a message
      if (Object.keys(changedFields).length === 0) {
        toast.info("No changes to save");
        setIsSaving(false);
        return;
      }
      
      console.log("Saving changed fields:", changedFields);
      
      // Use different endpoint for public view
      let response;
      if (isPublicView) {
        response = await axios.patch(
          `${API}/portal/${publicShareToken}/authenticated/records/${activePage.table_name}/${selectedRecord.id}`,
          { fields: changedFields },
          authHeaders
        );
      } else {
        response = await axios.patch(
          `${API}/airtable/records/${activePage.table_name}/${selectedRecord.id}?portal_id=${portalId}`,
          { fields: changedFields },
          authHeaders
        );
      }
      
      // Update the record in local state
      setRecords(records.map(r => 
        r.id === selectedRecord.id 
          ? { ...r, fields: response.data.fields }
          : r
      ));
      setSelectedRecord({ ...selectedRecord, fields: response.data.fields });
      setEditedFields(response.data.fields); // Update editedFields to match saved state
      setIsEditing(false);
      toast.success("Record updated successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update record";
      console.error("Save error:", error.response?.data);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchTableSchema = async () => {
    try {
      // Use different endpoint for public view
      let response;
      if (isPublicView) {
        response = await axios.get(
          `${API}/portal/${publicShareToken}/authenticated/schema`,
          authHeaders
        );
      } else {
        response = await axios.get(`${API}/airtable/schema?portal_id=${portalId}`, authHeaders);
      }
      setFullSchema(response.data);
      const table = response.data.tables?.find(t => t.name === activePage?.table_name);
      setTableSchema(table);
      return table;
    } catch (error) {
      console.error("Failed to fetch schema:", error);
      return null;
    }
  };

  // Fetch records from a linked table for the linked record selector
  const fetchLinkedTableRecords = async (linkedTableId, fieldName) => {
    // Find the table name from the schema using the table ID
    const linkedTable = fullSchema?.tables?.find(t => t.id === linkedTableId);
    if (!linkedTable) {
      console.error("Linked table not found:", linkedTableId);
      return;
    }
    
    // Check if already cached
    if (linkedRecordsCache[linkedTableId]) return;
    
    setLoadingLinkedRecords(prev => ({ ...prev, [fieldName]: true }));
    try {
      // Fetch ALL records by paginating through results
      let allRecords = [];
      let offset = null;
      
      do {
        const response = await axios.post(`${API}/airtable/records?portal_id=${portalId}`, {
          table_name: linkedTable.name,
          page_size: 100,
          offset: offset
        }, authHeaders);
        
        const records = (response.data.records || []).map(rec => ({
          id: rec.id,
          name: rec.fields?.[linkedTable.fields?.[0]?.name] || rec.id,
          fields: rec.fields
        }));
        
        allRecords = [...allRecords, ...records];
        offset = response.data.offset; // Get next page offset
      } while (offset);
      
      setLinkedRecordsCache(prev => ({
        ...prev,
        [linkedTableId]: allRecords
      }));
    } catch (error) {
      console.error("Failed to fetch linked records:", error);
    } finally {
      setLoadingLinkedRecords(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Preview a linked record
  const handlePreviewLinkedRecord = async (recordId, linkedTableId) => {
    const linkedTable = fullSchema?.tables?.find(t => t.id === linkedTableId);
    if (!linkedTable) return;
    
    setLoadingPreview(true);
    setPreviewDialogOpen(true);
    
    try {
      const response = await axios.get(
        `${API}/airtable/records/${linkedTable.name}/${recordId}?portal_id=${portalId}`,
        authHeaders
      );
      setPreviewRecord({
        ...response.data,
        tableName: linkedTable.name,
        tableFields: linkedTable.fields
      });
    } catch (error) {
      toast.error("Failed to load record preview");
      setPreviewDialogOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Open create new linked record dialog
  const handleOpenCreateLinkedDialog = (fieldName, linkedTableId) => {
    setCreateLinkedFieldName(fieldName);
    setCreateLinkedTableId(linkedTableId);
    setCreateFormLinkedSearch({});
    
    // Find the reverse link field in the linked table (field that links back to current table)
    const linkedTable = fullSchema?.tables?.find(t => t.id === linkedTableId);
    const currentTableId = fullSchema?.tables?.find(t => t.name === activePage?.table_name)?.id;
    
    // Look for a field in the linked table that links back to our current table
    const reverseLinkField = linkedTable?.fields?.find(f => 
      f.type === 'multipleRecordLinks' && f.options?.linkedTableId === currentTableId
    );
    
    // Auto-populate the reverse link field with current record
    if (reverseLinkField && selectedRecord?.id) {
      const selectedName = selectedRecord.fields?.[Object.keys(selectedRecord.fields || {})[0]] || selectedRecord.id;
      setNewLinkedRecordFields({
        [reverseLinkField.name]: [selectedRecord.id],
        [`__autoLinked_${reverseLinkField.name}`]: selectedName // Store display name for UI
      });
    } else {
      setNewLinkedRecordFields({});
    }
    
    setCreateLinkedDialogOpen(true);
  };

  // Fetch linked records for a field in the create form
  const fetchCreateFormLinkedRecords = async (tableId, fieldName) => {
    if (!tableId || createFormLinkedLoading[tableId]) {
      return;
    }
    
    // Check if already cached
    if (createFormLinkedCache[tableId]?.length > 0) {
      return;
    }
    
    const linkedTable = fullSchema?.tables?.find(t => t.id === tableId);
    if (!linkedTable) {
      console.log("No linkedTable found for ID:", tableId);
      return;
    }
    
    setCreateFormLinkedLoading(prev => ({ ...prev, [tableId]: true }));
    
    try {
      // Use POST with table_name in body (same as fetchLinkedTableRecords)
      const response = await axios.post(`${API}/airtable/records?portal_id=${portalId}`, {
        table_name: linkedTable.name,
        page_size: 100
      }, authHeaders);
      
      const records = (response.data.records || []).map(r => ({
        id: r.id,
        name: r.fields?.[linkedTable.fields?.[0]?.name] || r.id,
        fields: r.fields
      }));
      
      setCreateFormLinkedCache(prev => ({ ...prev, [tableId]: records }));
    } catch (error) {
      console.error("Failed to fetch linked records:", error);
    } finally {
      setCreateFormLinkedLoading(prev => ({ ...prev, [tableId]: false }));
    }
  };

  // Get linked table schema
  const getLinkedTableSchema = () => {
    return fullSchema?.tables?.find(t => t.id === createLinkedTableId);
  };

  // Create new linked record and add it to the field
  const handleCreateLinkedRecord = async () => {
    const linkedTable = getLinkedTableSchema();
    if (!linkedTable || !createLinkedFieldName) return;
    
    // Get create_config to check required fields
    const createConfig = activePage?.field_config?.[createLinkedFieldName]?.create_config;
    
    // Validate required fields
    if (createConfig?.field_settings) {
      const missingRequired = [];
      for (const [fieldName, settings] of Object.entries(createConfig.field_settings)) {
        if (settings.required) {
          const value = newLinkedRecordFields[fieldName];
          const isEmpty = value === null || value === undefined || value === '' || 
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0);
          if (isEmpty) {
            missingRequired.push(fieldName);
          }
        }
      }
      
      if (missingRequired.length > 0) {
        toast.error(`Please fill in required fields: ${missingRequired.join(', ')}`);
        return;
      }
    }
    
    setIsCreatingLinked(true);
    try {
      // Filter out null, undefined, empty values, and metadata fields (__autoLinked_)
      const cleanedFields = Object.fromEntries(
        Object.entries(newLinkedRecordFields).filter(([key, value]) => {
          // Skip metadata fields
          if (key.startsWith('__autoLinked_')) return false;
          if (value === null || value === undefined || value === '') return false;
          if (typeof value === 'string' && value.trim() === '') return false;
          return true;
        })
      );
      
      // Use different endpoint for public view
      let response;
      if (isPublicView) {
        response = await axios.post(
          `${API}/portal/${publicShareToken}/authenticated/records/${linkedTable.name}`,
          { fields: cleanedFields },
          authHeaders
        );
      } else {
        response = await axios.post(
          `${API}/airtable/records/create?portal_id=${portalId}`,
          { 
            table_name: linkedTable.name,
            fields: cleanedFields 
          },
          authHeaders
        );
      }
      
      // Add the new record to the cache
      const newRecord = {
        id: response.data.id,
        name: response.data.fields?.[linkedTable.fields?.[0]?.name] || response.data.id,
        fields: response.data.fields
      };
      
      setLinkedRecordsCache(prev => ({
        ...prev,
        [createLinkedTableId]: [...(prev[createLinkedTableId] || []), newRecord]
      }));
      
      // Add the new record ID to the edited field
      const currentValues = editedFields[createLinkedFieldName] || [];
      const newValues = Array.isArray(currentValues) 
        ? [...currentValues, response.data.id]
        : [response.data.id];
      
      setEditedFields(prev => ({
        ...prev,
        [createLinkedFieldName]: newValues
      }));
      
      setCreateLinkedDialogOpen(false);
      setNewLinkedRecordFields({}); // Reset the form
      toast.success("Record created and linked!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create record";
      toast.error(message);
    } finally {
      setIsCreatingLinked(false);
    }
  };

  // Fetch linked records for view mode (to show meaningful content)
  const fetchViewLinkedRecords = async (linkedTableId, recordIds) => {
    if (!linkedTableId || !recordIds || recordIds.length === 0) return;
    
    // Find the table name from schema - if schema not loaded yet, try to load it
    let schema = fullSchema;
    if (!schema) {
      try {
        // Use different endpoint for public view
        let response;
        if (isPublicView) {
          response = await axios.get(
            `${API}/portal/${publicShareToken}/authenticated/schema`,
            authHeaders
          );
        } else {
          response = await axios.get(`${API}/airtable/schema?portal_id=${portalId}`, authHeaders);
        }
        schema = response.data;
        setFullSchema(schema);
      } catch (error) {
        console.error("Failed to fetch schema:", error);
        return;
      }
    }
    
    const linkedTable = schema?.tables?.find(t => t.id === linkedTableId);
    if (!linkedTable) {
      console.warn("fetchViewLinkedRecords: Linked table not found:", linkedTableId);
      return;
    }
    
    // Check which records are not already cached - use ref to get latest cache state
    const currentCache = viewLinkedRecordsCacheRef.current;
    const uncachedIds = recordIds.filter(id => 
      typeof id === 'string' && 
      id.startsWith('rec') && 
      !currentCache[id]
    );
    
    if (uncachedIds.length === 0) {
      // All records are already cached, no need to fetch or update state
      return;
    }
    
    const cacheKey = `${linkedTableId}_loading`;
    if (loadingViewLinkedRecords[cacheKey]) {
      return;
    }
    
    setLoadingViewLinkedRecords(prev => ({ ...prev, [cacheKey]: true }));
    
    try {
      // Use batch endpoint to fetch all records in a single request
      // Use different endpoint for public view
      let response;
      if (isPublicView) {
        response = await axios.post(
          `${API}/portal/${publicShareToken}/authenticated/records/batch`,
          {
            table_name: linkedTable.name,
            record_ids: uncachedIds.slice(0, 100) // Limit to 100 records per batch
          },
          authHeaders
        );
      } else {
        response = await axios.post(
          `${API}/airtable/records/batch?portal_id=${portalId}`,
          {
            table_name: linkedTable.name,
            record_ids: uncachedIds.slice(0, 100) // Limit to 100 records per batch
          },
          authHeaders
        );
      }
      
      const fetchedRecords = response.data.records || [];
      
      // Use functional setState to avoid race conditions when multiple tables are being fetched
      // Also update the ref synchronously so render functions can access latest data immediately
      setViewLinkedRecordsCache(prevCache => {
        const newCache = { ...prevCache };
        fetchedRecords.forEach(rec => {
          if (rec && rec.id) {
            newCache[rec.id] = {
              id: rec.id,
              fields: rec.fields,
              tableName: linkedTable.name,
              tableFields: linkedTable.fields
            };
          }
        });
        // Update ref synchronously for immediate access in render
        viewLinkedRecordsCacheRef.current = newCache;
        return newCache;
      });
    } catch (error) {
      console.error("Failed to fetch linked records for view:", error);
    } finally {
      setLoadingViewLinkedRecords(prev => ({ ...prev, [cacheKey]: false }));
    }
  };

  // Open expanded linked record dialog
  const handleExpandLinkedRecord = async (recordId, linkedTableId, parentFieldName = null) => {
    const linkedTable = fullSchema?.tables?.find(t => t.id === linkedTableId);
    if (!linkedTable) return;
    
    setLoadingExpandedRecord(true);
    setExpandedLinkedDialogOpen(true);
    setExpandedLinkedParentField(parentFieldName);
    
    try {
      let response;
      if (publicShareToken) {
        // Public portal user - use authenticated endpoint with portal user token
        response = await axios.get(
          `${API}/portal/${publicShareToken}/authenticated/records/${linkedTable.name}/${recordId}`,
          {
            headers: publicUserToken ? { 'X-Portal-User-Token': publicUserToken } : {}
          }
        );
      } else {
        // Admin user - use admin endpoint
        response = await axios.get(
          `${API}/airtable/records/${linkedTable.name}/${recordId}?portal_id=${portalId}`,
          authHeaders
        );
      }
      setExpandedLinkedRecord({
        ...response.data,
        tableName: linkedTable.name,
        tableFields: linkedTable.fields
      });
    } catch (error) {
      toast.error("Failed to load record details");
      setExpandedLinkedDialogOpen(false);
    } finally {
      setLoadingExpandedRecord(false);
    }
  };

  const handleStartEdit = async () => {
    setEditedFields({ ...selectedRecord.fields });
    // Fetch schema to get field types and options
    const schema = await fetchTableSchema();
    setLinkedRecordSearch({});
    setIsEditing(true);
    
    // Pre-fetch linked records for any multipleRecordLinks fields that have values
    if (schema && selectedRecord?.fields) {
      for (const field of schema.fields || []) {
        if (field.type === 'multipleRecordLinks') {
          const linkedTableId = field.options?.linkedTableId;
          const fieldValue = selectedRecord.fields[field.name];
          
          // If field has values and we have a linkedTableId, fetch the records
          if (linkedTableId && Array.isArray(fieldValue) && fieldValue.length > 0) {
            fetchLinkedTableRecords(linkedTableId, field.name);
          }
        }
      }
    }
  };

  // Check if a field is editable based on field_config
  const isFieldEditable = (fieldName) => {
    const fieldType = getFieldSchema(fieldName)?.type || 'text';
    
    // Auto-readonly field types (formula, rollup, lookup, etc.)
    const readOnlyTypes = ['autoNumber', 'formula', 'rollup', 'lookup', 'count', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy', 'multipleLookupValues'];
    if (readOnlyTypes.includes(fieldType)) return false;
    
    // Check field_config for allow_edit
    const fieldConfig = activePage?.field_config || {};
    return fieldConfig[fieldName]?.allow_edit === true;
  };

  // Start inline editing for a specific field
  const startInlineEdit = async (fieldName) => {
    // Fetch schema if not loaded
    if (!tableSchema) {
      await fetchTableSchema();
    }
    
    const fieldType = getFieldSchema(fieldName)?.type;
    const linkedTableId = getFieldSchema(fieldName)?.options?.linkedTableId;
    
    // Pre-fetch linked records for multipleRecordLinks fields
    if (fieldType === 'multipleRecordLinks' && linkedTableId) {
      fetchLinkedTableRecords(linkedTableId, fieldName);
    }
    
    setEditingFieldName(fieldName);
    setEditedFieldValue(selectedRecord?.fields?.[fieldName]);
  };

  // Save inline field edit
  const saveInlineEdit = async () => {
    if (!editingFieldName || !selectedRecord || !activePage?.table_name) return;
    
    setIsSaving(true);
    try {
      const updatedFields = { [editingFieldName]: editedFieldValue };
      
      // Use different endpoint for public view
      let response;
      if (isPublicView) {
        response = await axios.patch(
          `${API}/portal/${publicShareToken}/authenticated/records/${activePage.table_name}/${selectedRecord.id}`,
          { fields: updatedFields },
          { headers: publicUserToken ? { 'X-Portal-User-Token': publicUserToken } : {} }
        );
      } else {
        response = await axios.patch(
          `${API}/airtable/records/${activePage.table_name}/${selectedRecord.id}?portal_id=${portalId}`,
          { fields: updatedFields },
          authHeaders
        );
      }
      
      // Update local state with response from server (not local value)
      const serverFields = response.data.fields || {};
      const updatedRecord = { ...selectedRecord, fields: { ...selectedRecord.fields, ...serverFields } };
      setSelectedRecord(updatedRecord);
      setRecords(records.map(r => r.id === selectedRecord.id ? updatedRecord : r));
      
      // IMPORTANT: Also update editedFields so "Save & Exit" has the correct values
      setEditedFields(prev => ({ ...prev, ...serverFields }));
      
      toast.success(`${editingFieldName} updated successfully`);
      setEditingFieldName(null);
      setEditedFieldValue(null);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to update field";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel inline field edit
  const cancelInlineEdit = () => {
    setEditingFieldName(null);
    setEditedFieldValue(null);
  };

  // Helper function to check if file is an image
  const isImageFile = (file) => {
    if (!file) return false;
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (file.type && imageTypes.includes(file.type.toLowerCase())) return true;
    const ext = (file.filename || file.url || '').toLowerCase();
    return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png') || 
           ext.endsWith('.gif') || ext.endsWith('.webp') || ext.endsWith('.svg');
  };

  // Get thumbnail URL for Airtable attachments
  const getThumbnailUrl = (file) => {
    if (file.thumbnails?.large?.url) return file.thumbnails.large.url;
    if (file.thumbnails?.small?.url) return file.thumbnails.small.url;
    if (isImageFile(file)) return file.url;
    return null;
  };

  // Get file extension
  const getFileExtension = (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toUpperCase() : '';
  };

  // Open file preview
  const openFilePreview = (file) => {
    setPreviewFile(file);
    setPreviewFileDialogOpen(true);
  };

  // Handle file upload for attachments - uploads to R2 and then updates Airtable
  const handleFileUpload = async (e, fieldName) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingFile(true);
    setUploadFieldName(fieldName);
    
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        // Upload to R2 via our API
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadResponse = await axios.post(
          `${API}/upload-file?portal_id=${portalId}`,
          formData,
          {
            ...authHeadersWithCsrf,
            headers: {
              ...authHeadersWithCsrf.headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        // R2 returns a public URL that Airtable can access
        uploadedFiles.push({
          url: uploadResponse.data.url,
          filename: file.name
        });
      }
      
      // Get existing attachments
      const existingAttachments = selectedRecord?.fields?.[fieldName] || [];
      const newAttachments = [...existingAttachments, ...uploadedFiles];
      
      // Update via Airtable API
      const response = await axios.patch(
        `${API}/airtable/records/${activePage.table_name}/${selectedRecord.id}?portal_id=${portalId}`,
        { fields: { [fieldName]: newAttachments } },
        authHeadersWithCsrf
      );
      
      // Update local state
      const updatedRecord = { 
        ...selectedRecord, 
        fields: { ...selectedRecord.fields, [fieldName]: response.data.fields?.[fieldName] || newAttachments } 
      };
      setSelectedRecord(updatedRecord);
      setRecords(records.map(r => r.id === selectedRecord.id ? updatedRecord : r));
      
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      console.error("File upload error:", error);
      const message = error.response?.data?.detail || "Failed to upload file";
      toast.error(message);
    } finally {
      setUploadingFile(false);
      setUploadFieldName(null);
      e.target.value = ''; // Reset file input
    }
  };

  // Handle file upload for create form - uploads to R2 and stores URLs for later
  const handleCreateFormFileUpload = async (e, fieldName) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingFile(true);
    setUploadFieldName(fieldName);
    
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        // Upload to R2 via our API
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadResponse = await axios.post(
          `${API}/upload-file?portal_id=${portalId}`,
          formData,
          {
            ...authHeadersWithCsrf,
            headers: {
              ...authHeadersWithCsrf.headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        uploadedFiles.push({
          url: uploadResponse.data.url,
          filename: file.name
        });
      }
      
      // Add to pending files for this field
      setCreateFormPendingFiles(prev => ({
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), ...uploadedFiles]
      }));
      
      toast.success(`${files.length} file(s) ready to attach`);
    } catch (error) {
      console.error("File upload error:", error);
      const message = error.response?.data?.detail || "Failed to upload file";
      toast.error(message);
    } finally {
      setUploadingFile(false);
      setUploadFieldName(null);
      e.target.value = '';
    }
  };

  // Remove a pending file from create form
  const removeCreateFormPendingFile = (fieldName, index) => {
    setCreateFormPendingFiles(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index)
    }));
  };

  // Render file/attachment thumbnails
  const renderAttachmentThumbnails = (files, fieldName, allowUpload = false) => {
    const fileArray = Array.isArray(files) ? files : [];
    const fieldEditable = isFieldEditable(fieldName);
    
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {fileArray.map((file, idx) => {
            const thumbnailUrl = getThumbnailUrl(file);
            const isImage = isImageFile(file);
            const ext = getFileExtension(file.filename);
            
            return (
              <div
                key={idx}
                className="relative group cursor-pointer rounded-lg border border-border overflow-hidden bg-muted/30 hover:border-primary transition-colors aspect-square"
                onClick={() => openFilePreview(file)}
              >
                {isImage && thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt={file.filename || 'Attachment'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-2">
                    <File className="w-8 h-8 text-muted-foreground mb-1" />
                    <span className="text-xs text-muted-foreground font-medium">{ext}</span>
                  </div>
                )}
                {/* Overlay with view icon */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="w-6 h-6 text-white" />
                </div>
                {/* Filename tooltip */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-xs text-white truncate">{file.filename || 'File'}</p>
                </div>
              </div>
            );
          })}
          
          {/* Upload button */}
          {allowUpload && fieldEditable && (
            <label className="relative cursor-pointer rounded-lg border-2 border-dashed border-border hover:border-primary transition-colors aspect-square flex flex-col items-center justify-center bg-muted/20 hover:bg-muted/40">
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(e, fieldName)}
                disabled={uploadingFile}
              />
              {uploadingFile && uploadFieldName === fieldName ? (
                <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
              ) : (
                <>
                  <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                  <span className="text-xs text-muted-foreground">Add File</span>
                </>
              )}
            </label>
          )}
        </div>
        
        {fileArray.length === 0 && !allowUpload && (
          <p className="text-sm text-muted-foreground">No files attached</p>
        )}
      </div>
    );
  };

  // Render attachments for a field - handles both object arrays and string format from cellFormat=string
  const renderAttachmentsForField = (value, fieldName) => {
    // If it's already an array of objects with url property, use thumbnails directly
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0].url) {
      return renderAttachmentThumbnails(value, fieldName, isFieldEditable(fieldName));
    }
    
    // Parse string format: "filename.pdf (https://...)" or comma-separated list
    // Format from cellFormat=string: "filename (url), filename2 (url2)"
    let parsedFiles = [];
    
    if (typeof value === 'string' && value.trim()) {
      // Split by common separators, but be careful with URLs that have commas
      // Format is typically: "filename.ext (https://full-url)"
      const fileRegex = /([^,()]+\.[a-zA-Z0-9]+)\s*\((https?:\/\/[^\s)]+)\)/g;
      let match;
      
      while ((match = fileRegex.exec(value)) !== null) {
        const filename = match[1].trim();
        const url = match[2].trim();
        parsedFiles.push({
          filename,
          url,
          type: getFileTypeFromExtension(filename)
        });
      }
      
      // If regex didn't match, try to extract URLs directly
      if (parsedFiles.length === 0) {
        const urlRegex = /(https?:\/\/[^\s,)]+)/g;
        let urlMatch;
        while ((urlMatch = urlRegex.exec(value)) !== null) {
          parsedFiles.push({
            filename: 'File',
            url: urlMatch[1],
            type: 'application/octet-stream'
          });
        }
      }
    } else if (Array.isArray(value)) {
      // Handle array of strings
      value.forEach(item => {
        if (typeof item === 'string' && item.includes('http')) {
          const urlMatch = item.match(/(https?:\/\/[^\s,)]+)/);
          if (urlMatch) {
            const filenameMatch = item.match(/^([^(]+)/);
            parsedFiles.push({
              filename: filenameMatch ? filenameMatch[1].trim() : 'File',
              url: urlMatch[1],
              type: 'application/octet-stream'
            });
          }
        }
      });
    }
    
    if (parsedFiles.length > 0) {
      return renderAttachmentThumbnails(parsedFiles, fieldName, isFieldEditable(fieldName));
    }
    
    // Fallback: display raw value
    return <span className="text-muted-foreground">{String(value)}</span>;
  };

  // Get file type from extension
  const getFileTypeFromExtension = (filename) => {
    if (!filename) return 'application/octet-stream';
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeTypes = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg', 'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'mp3': 'audio/mpeg',
      'mp4': 'video/mp4',
      'mov': 'video/quicktime'
    };
    return mimeTypes[ext] || 'application/octet-stream';
  };

  const handleOpenCreateDialog = async () => {
    await fetchTableSchema();
    setNewRecordFields({});
    setLinkedRecordSearch({});
    setCreateDialogOpen(true);
  };

  // Get field schema by name
  const getFieldSchema = (fieldName) => {
    return tableSchema?.fields?.find(f => f.name === fieldName);
  };

  // Render appropriate input based on field type
  const renderFieldInput = (fieldName, value, onChange, isCreate = false) => {
    const fieldSchema = getFieldSchema(fieldName);
    const fieldType = fieldSchema?.type || 'singleLineText';
    const options = fieldSchema?.options || {};

    // Read-only field types
    const readOnlyTypes = ['autoNumber', 'formula', 'rollup', 'lookup', 'count', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy'];
    if (readOnlyTypes.includes(fieldType) && !isCreate) {
      return (
        <div className="text-muted-foreground italic text-sm p-2 bg-muted/30 rounded">
          {value || '(auto-generated)'}
        </div>
      );
    }

    switch (fieldType) {
      case 'singleSelect':
        const singleChoices = options.choices || [];
        return (
          <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? null : v)}>
            <SelectTrigger className="bg-background border-border text-foreground">
              <SelectValue placeholder={`Select ${fieldName}`} />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="__none__">
                <span className="text-muted-foreground">None</span>
              </SelectItem>
              {singleChoices.map((choice) => (
                <SelectItem key={choice.id || choice.name} value={choice.name}>
                  <div className="flex items-center gap-2">
                    {choice.color && (
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: getAirtableColor(choice.color) }}
                      />
                    )}
                    {choice.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'multipleSelects':
        const multiChoices = options.choices || [];
        const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 min-h-[36px] p-2 bg-background border border-border rounded-md">
              {selectedValues.length === 0 ? (
                <span className="text-muted-foreground text-sm">Click options below to select</span>
              ) : (
                selectedValues.map((v, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded text-sm cursor-pointer hover:bg-primary/30"
                    onClick={() => {
                      const newValues = selectedValues.filter(sv => sv !== v);
                      onChange(newValues.length > 0 ? newValues : null);
                    }}
                  >
                    {v}
                    <X className="w-3 h-3" />
                  </span>
                ))
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {multiChoices.filter(c => !selectedValues.includes(c.name)).map((choice) => (
                <button
                  key={choice.id || choice.name}
                  type="button"
                  className="inline-flex items-center gap-1 px-2 py-1 bg-muted hover:bg-muted/80 rounded text-sm transition-colors"
                  onClick={() => {
                    onChange([...selectedValues, choice.name]);
                  }}
                >
                  <Plus className="w-3 h-3" />
                  {choice.name}
                </button>
              ))}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={!!value}
              onCheckedChange={(checked) => onChange(checked)}
              className="border-border data-[state=checked]:bg-primary"
            />
            <span className="text-sm text-muted-foreground">
              {value ? 'Yes' : 'No'}
            </span>
          </div>
        );

      case 'number':
      case 'currency':
      case 'percent':
        return (
          <Input
            type="number"
            className="bg-background border-border text-foreground"
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            placeholder={`Enter ${fieldName}`}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            className="bg-background border-border text-foreground"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
          />
        );

      case 'dateTime':
        return (
          <Input
            type="datetime-local"
            className="bg-background border-border text-foreground"
            value={value ? value.slice(0, 16) : ''}
            onChange={(e) => onChange(e.target.value || null)}
          />
        );

      case 'email':
        return (
          <Input
            type="email"
            className="bg-background border-border text-foreground"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder={`Enter ${fieldName}`}
          />
        );

      case 'url':
        return (
          <Input
            type="url"
            className="bg-background border-border text-foreground"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder="https://..."
          />
        );

      case 'phoneNumber':
        return (
          <Input
            type="tel"
            className="bg-background border-border text-foreground"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder={`Enter ${fieldName}`}
          />
        );

      case 'multilineText':
        return (
          <Textarea
            className="bg-background border-border text-foreground"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            rows={3}
            placeholder={`Enter ${fieldName}`}
          />
        );

      case 'multipleRecordLinks':
        const linkedTableId = options.linkedTableId;
        const linkedValues = Array.isArray(value) ? value : (value ? [value] : []);
        const linkedTableRecords = linkedRecordsCache[linkedTableId] || [];
        const isLoadingLinked = loadingLinkedRecords[fieldName];
        const searchTermLinked = linkedRecordSearch[fieldName] || '';
        const allowCreateNew = activePage?.field_config?.[fieldName]?.allow_create_linked || false;
        
        // Helper to get display name and record ID for a linked value
        const getLinkedRecordInfo = (v) => {
          // If it's a record ID (starts with 'rec'), look up the record
          if (typeof v === 'string' && v.startsWith('rec')) {
            const cachedRec = linkedTableRecords.find(r => r.id === v);
            if (cachedRec) return { id: v, name: cachedRec.name || v, fields: cachedRec.fields };
            return { id: v, name: v, fields: null };
          }
          // Otherwise, it's a display name - try to find the record
          const cleanedV = cleanLinkedRecordValue(String(v));
          const cachedRec = linkedTableRecords.find(r => r.name === cleanedV);
          return { id: cachedRec?.id || null, name: cleanedV, fields: cachedRec?.fields || null };
        };
        
        // Filter available records (not already selected and matching search)
        const availableRecords = linkedTableRecords.filter(rec => {
          const recName = String(rec.name || rec.id);
          const isSelected = linkedValues.some(v => {
            // Check if selected by ID or by display name
            if (v === rec.id) return true;
            const cleanedV = cleanLinkedRecordValue(String(v));
            return cleanedV === recName;
          });
          const matchesSearch = !searchTermLinked || recName.toLowerCase().includes(searchTermLinked.toLowerCase());
          return !isSelected && matchesSearch;
        });
        
        return (
          <div className="space-y-2">
            {/* Selected linked records */}
            <div className="flex flex-wrap gap-2 min-h-[36px] p-2 bg-background border border-border rounded-md">
              {linkedValues.length === 0 ? (
                <span className="text-muted-foreground text-sm">No linked records</span>
              ) : (
                linkedValues.map((v, idx) => {
                  const recInfo = getLinkedRecordInfo(v);
                  return (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded text-sm group"
                    >
                      {/* Preview button */}
                      <button
                        type="button"
                        className="hover:text-blue-500 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (recInfo.id && linkedTableId) {
                            handlePreviewLinkedRecord(recInfo.id, linkedTableId);
                          }
                        }}
                        title="Preview record"
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                      <span>{recInfo.name}</span>
                      {/* Remove button */}
                      <button
                        type="button"
                        className="hover:text-destructive transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newValues = linkedValues.filter((_, i) => i !== idx);
                          onChange(newValues.length > 0 ? newValues : null);
                        }}
                        title="Remove"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })
              )}
            </div>
            
            {/* Search and add linked records */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-10 bg-background border-border text-foreground"
                  placeholder="Search to add linked records..."
                  value={searchTermLinked}
                  onChange={(e) => setLinkedRecordSearch(prev => ({ ...prev, [fieldName]: e.target.value }))}
                  onFocus={() => {
                    if (!linkedRecordsCache[linkedTableId] && linkedTableId) {
                      fetchLinkedTableRecords(linkedTableId, fieldName);
                    }
                  }}
                />
              </div>
              
              {isLoadingLinked ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading linked records...
                </div>
              ) : linkedTableRecords.length > 0 ? (
                <div className="max-h-40 overflow-y-auto border border-border rounded-md">
                  {availableRecords.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      {searchTermLinked ? 'No matching records' : 'All records already linked'}
                    </div>
                  ) : (
                    availableRecords.slice(0, 20).map((rec) => (
                      <button
                        key={rec.id}
                        type="button"
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted transition-colors border-b border-border/50 last:border-0"
                        onClick={() => {
                          // Add the record ID to the linked values
                          onChange([...linkedValues, rec.id]);
                          setLinkedRecordSearch(prev => ({ ...prev, [fieldName]: '' }));
                        }}
                      >
                        <Plus className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="truncate">{rec.name || rec.id}</span>
                      </button>
                    ))
                  )}
                  {availableRecords.length > 20 && (
                    <div className="p-2 text-xs text-muted-foreground text-center border-t border-border">
                      Showing first 20 results. Type to search for more.
                    </div>
                  )}
                  {/* Add New button when allowed */}
                  {allowCreateNew && (
                    <button
                      type="button"
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow-md hover:shadow-lg transition-all border-t border-emerald-400"
                      onClick={() => handleOpenCreateLinkedDialog(fieldName, linkedTableId)}
                    >
                      <Plus className="w-4 h-4 flex-shrink-0" />
                      <span>CREATE NEW</span>
                    </button>
                  )}
                </div>
              ) : linkedTableId ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    className="w-full text-sm text-primary hover:underline text-left"
                    onClick={() => fetchLinkedTableRecords(linkedTableId, fieldName)}
                  >
                    Click to load available records
                  </button>
                  {allowCreateNew && (
                    <button
                      type="button"
                      className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-md shadow-md hover:shadow-lg transition-all"
                      onClick={() => handleOpenCreateLinkedDialog(fieldName, linkedTableId)}
                    >
                      <Plus className="w-4 h-4 flex-shrink-0" />
                      <span>CREATE NEW</span>
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Unable to load linked records
                </p>
              )}
            </div>
          </div>
        );

      case 'multipleAttachments':
        // For create form, allow file uploads
        if (isCreate) {
          const pendingFiles = createFormPendingFiles[fieldName] || [];
          return (
            <div className="space-y-3">
              {/* Show pending files */}
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {pendingFiles.map((file, idx) => (
                    <div key={idx} className="relative group">
                      <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center border border-border">
                        {file.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <img src={file.url} alt={file.filename} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <File className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCreateFormPendingFile(fieldName, idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <p className="text-xs text-muted-foreground truncate w-20 mt-1">{file.filename}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Upload button */}
              <label className="flex items-center gap-2 px-4 py-3 bg-muted/50 border border-dashed border-border rounded-lg text-muted-foreground hover:bg-muted/70 cursor-pointer transition-colors">
                {uploadingFile && uploadFieldName === fieldName ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span className="text-sm">Click to upload files</span>
                  </>
                )}
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleCreateFormFileUpload(e, fieldName)}
                  disabled={uploadingFile}
                />
              </label>
            </div>
          );
        }
        // For edit mode, use existing handler
        return renderAttachmentThumbnails(value, fieldName, true);

      case 'rating':
        const maxRating = options.max || 5;
        return (
          <div className="flex items-center gap-1">
            {Array.from({ length: maxRating }, (_, i) => (
              <button
                key={i}
                type="button"
                className={`w-6 h-6 rounded ${
                  i < (value || 0) ? 'bg-yellow-500 text-white' : 'bg-muted text-muted-foreground'
                }`}
                onClick={() => onChange(i + 1)}
              >
                ★
              </button>
            ))}
            {value > 0 && (
              <button
                type="button"
                className="ml-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => onChange(null)}
              >
                Clear
              </button>
            )}
          </div>
        );

      default:
        // singleLineText and others
        return (
          <Input
            className="bg-background border-border text-foreground"
            value={value || ''}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder={`Enter ${fieldName}`}
          />
        );
    }
  };

  // Helper to get Airtable color
  const getAirtableColor = (colorName) => {
    const colors = {
      blueLight2: '#D0E0FC', blueDark1: '#2D7FF9', blue: '#2D7FF9',
      cyanLight2: '#D0F0FD', cyanDark1: '#18BFFF', cyan: '#18BFFF',
      tealLight2: '#C2F5E9', tealDark1: '#20D9D2', teal: '#20D9D2',
      greenLight2: '#D1F7C4', greenDark1: '#20C933', green: '#20C933',
      yellowLight2: '#FFEAB6', yellowDark1: '#FCB400', yellow: '#FCB400',
      orangeLight2: '#FEE2D5', orangeDark1: '#FF6F2C', orange: '#FF6F2C',
      redLight2: '#FFDCE5', redDark1: '#F82B60', red: '#F82B60',
      pinkLight2: '#FFDAF6', pinkDark1: '#FF08C2', pink: '#FF08C2',
      purpleLight2: '#EDE2FE', purpleDark1: '#8B46FF', purple: '#8B46FF',
      grayLight2: '#EAEAEA', grayDark1: '#666666', gray: '#666666',
    };
    return colors[colorName] || '#666666';
  };

  const handleCreateRecord = async () => {
    if (!activePage) return;
    
    // Check required fields
    const createFormConfig = activePage?.create_form_config;
    const fieldSettings = createFormConfig?.field_settings || {};
    const requiredFields = Object.entries(fieldSettings)
      .filter(([_, settings]) => settings?.required)
      .map(([fieldName]) => fieldName);
    
    // Validate required fields
    for (const fieldName of requiredFields) {
      const value = newRecordFields[fieldName];
      if (value === undefined || value === null || value === '' || 
          (Array.isArray(value) && value.length === 0)) {
        toast.error(`"${fieldName}" is required`);
        return;
      }
    }
    
    setIsCreating(true);
    try {
      // Prepare fields to send, including file attachments uploaded to R2
      const fieldsToSend = { ...newRecordFields };
      
      // Get attachment field names
      const attachmentFields = (tableSchema?.fields || [])
        .filter(f => f.type === 'multipleAttachments')
        .map(f => f.name);
      
      // Add pending R2 file uploads to the fields
      for (const attachmentField of attachmentFields) {
        const pendingFiles = createFormPendingFiles[attachmentField];
        if (pendingFiles && pendingFiles.length > 0) {
          // Format for Airtable: array of objects with 'url' property
          fieldsToSend[attachmentField] = pendingFiles.map(f => ({ url: f.url }));
        }
        // Remove any base64 data that might have snuck in
        if (fieldsToSend[attachmentField] && Array.isArray(fieldsToSend[attachmentField])) {
          fieldsToSend[attachmentField] = fieldsToSend[attachmentField].filter(
            f => f.url && !f.url.startsWith('data:')
          );
          if (fieldsToSend[attachmentField].length === 0) {
            delete fieldsToSend[attachmentField];
          }
        }
      }
      
      // Use different endpoint for public view
      let response;
      if (isPublicView) {
        response = await axios.post(
          `${API}/portal/${publicShareToken}/authenticated/records/${activePage.table_name}`,
          { fields: fieldsToSend },
          authHeadersWithCsrf
        );
      } else {
        response = await axios.post(
          `${API}/airtable/records/create?portal_id=${portalId}`,
          { 
            table_name: activePage.table_name,
            fields: fieldsToSend 
          },
          authHeadersWithCsrf
        );
      }
      
      // Add to local state
      setRecords([response.data, ...records]);
      setCreateDialogOpen(false);
      setNewRecordFields({});
      setCreateFormPendingFiles({}); // Clear pending files
      
      toast.success("Record created successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create record";
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  // Get all unique field names from records
  const fields = useMemo(() => {
    // If we have visible_fields configured, use that order directly
    // Don't filter by record fields - show all configured fields
    if (activePage?.visible_fields?.length > 0) {
      return activePage.visible_fields;
    }
    
    // Fall back to fields that actually have data in records
    const fieldSet = new Set();
    records.forEach((record) => {
      Object.keys(record.fields || {}).forEach((key) => fieldSet.add(key));
    });
    
    // If we have tableSchema, use its field order
    if (tableSchema?.fields) {
      return tableSchema.fields
        .map(f => f.name)
        .filter(f => fieldSet.has(f));
    }
    
    return Array.from(fieldSet);
  }, [records, activePage, tableSchema]);

  // Get filterable fields for the current page
  const getFilterableFields = useMemo(() => {
    if (!activePage || !tableSchema) return [];
    const fieldConfig = activePage.field_config || {};
    
    return tableSchema.fields
      .filter(field => fieldConfig[field.name]?.is_filterable)
      .slice(0, 8); // Max 8 filters
  }, [activePage, tableSchema]);

  // Get filter options for a field
  const getFilterOptions = (field) => {
    if (field.type === 'singleSelect' || field.type === 'multipleSelects') {
      return field.options?.choices?.map(c => c.name) || [];
    }
    if (field.type === 'checkbox') {
      return ['Yes', 'No'];
    }
    return [];
  };

  // Toggle a filter value
  const toggleFilterValue = (fieldName, value) => {
    setActiveFilters(prev => {
      const currentValues = prev[fieldName] || [];
      if (currentValues.includes(value)) {
        const newValues = currentValues.filter(v => v !== value);
        if (newValues.length === 0) {
          const { [fieldName]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [fieldName]: newValues };
      } else {
        return { ...prev, [fieldName]: [...currentValues, value] };
      }
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters({});
  };

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    return Object.keys(activeFilters).length;
  }, [activeFilters]);

  // Sort and filter records (search is now done server-side, filters are client-side)
  const filteredRecords = useMemo(() => {
    let result = [...records];

    // Client-side filtering by Quick Filter Chips
    if (Object.keys(activeFilters).length > 0) {
      result = result.filter(record => {
        return Object.entries(activeFilters).every(([fieldName, selectedValues]) => {
          if (selectedValues.length === 0) return true;
          
          const fieldValue = record.fields?.[fieldName];
          const fieldDef = tableSchema?.fields?.find(f => f.name === fieldName);
          
          if (fieldDef?.type === 'checkbox') {
            const boolValue = fieldValue ? 'Yes' : 'No';
            return selectedValues.includes(boolValue);
          }
          
          if (fieldDef?.type === 'multipleSelects') {
            // For multi-select, check if any selected filter values are in the record's values
            if (Array.isArray(fieldValue)) {
              return selectedValues.some(sv => fieldValue.includes(sv));
            }
            return selectedValues.includes(fieldValue);
          }
          
          // For single select
          return selectedValues.includes(fieldValue);
        });
      });
    }

    // Client-side sorting only (search is server-side)
    if (sortField) {
      result.sort((a, b) => {
        const aVal = a.fields?.[sortField] ?? "";
        const bVal = b.fields?.[sortField] ?? "";
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return result;
  }, [records, sortField, sortDirection, activeFilters, tableSchema]);

  // Format date to dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  // Format datetime to dd/mm/yyyy hh:mm
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  // Format cell value for display
  // Clean up Airtable linked record format ("0, Value" -> "Value")
  // Airtable's cellFormat=string returns linked records with count prefix
  const cleanLinkedRecordValue = (value) => {
    if (typeof value !== 'string') return value;
    // Match pattern: "number, rest of string" where number is just the count prefix
    const match = value.match(/^\d+,\s*(.+)$/);
    if (match) {
      return match[1];
    }
    return value;
  };

  // Format cell value for display
  const formatValue = (value, fieldName = null, fieldType = null) => {
    if (value === null || value === undefined) return "-";
    
    // Handle date formatting
    if (fieldType === 'date') {
      return formatDate(value);
    }
    if (fieldType === 'dateTime') {
      return formatDateTime(value);
    }
    
    if (Array.isArray(value)) {
      // Check if these are attachment objects
      if (value.length > 0 && typeof value[0] === 'object' && value[0].url) {
        return value.map(v => v.filename || v.url).join(", ");
      }
      // Check if these are linked record IDs - use cached data if available
      if (value.length > 0 && typeof value[0] === 'string' && value[0].startsWith('rec')) {
        const displayValues = value.map(recordId => {
          const cached = viewLinkedRecordsCache[recordId];
          if (cached && cached.fields) {
            // Find a meaningful display field - try common primary field patterns
            const fields = cached.fields;
            const priorityNames = ['Full Name', 'Name', 'Title', 'Subject', 'Triage Pathway', 'Kaimahi Name', 'Note Subject', 'Email'];
            
            // Try to find a priority field first
            for (const name of priorityNames) {
              if (fields[name] !== undefined && fields[name] !== null) {
                const val = fields[name];
                // Skip if the value itself is a record ID
                if (typeof val === 'string' && !val.startsWith('rec')) {
                  return cleanLinkedRecordValue(String(val));
                }
              }
            }
            
            // Fallback to first non-ID, non-empty string field
            for (const [key, val] of Object.entries(fields)) {
              if (val !== null && val !== undefined && typeof val === 'string' && !val.startsWith('rec') && val.trim()) {
                return cleanLinkedRecordValue(String(val));
              }
            }
            
            // Last fallback: first field value
            const firstField = Object.keys(fields)[0];
            if (firstField) {
              return cleanLinkedRecordValue(String(fields[firstField]));
            }
          }
          return recordId; // Fallback to record ID
        });
        return displayValues.join(", ");
      }
      return value.join(", ");
    }
    if (typeof value === "object") {
      if (value.url) return value.filename || value.url;
      return JSON.stringify(value);
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    // Clean up linked record format
    return cleanLinkedRecordValue(String(value));
  };

  // Render linked records with rich cards (for view mode)
  const renderLinkedRecordsView = (value, fieldName, linkedTableId) => {
    // Get field configuration for this field
    const fieldConfig = activePage?.field_config?.[fieldName] || {};
    const allowExpand = fieldConfig.allow_expand !== false; // Default to true for backward compatibility
    const allowCreateView = fieldConfig.allow_create_view === true;
    const previewFields = fieldConfig.preview_fields || []; // Configured preview fields
    
    // Handle both array and comma-separated string formats
    // Airtable with cellFormat=string returns linked records as comma-separated strings
    let recordValues = [];
    
    if (Array.isArray(value)) {
      recordValues = value;
    } else if (typeof value === 'string' && value.trim()) {
      // Split comma-separated string and trim each value
      recordValues = value.split(',').map(v => v.trim()).filter(v => v);
    }
    
    // Reverse to show newest first (assuming newer records are at the end)
    recordValues = [...recordValues].reverse();
    
    // Check if this field is expanded (showing all) or collapsed (showing 4)
    const isExpanded = expandedLinkedFields[fieldName] === true;
    const maxVisible = 4;
    const hasMoreRecords = recordValues.length > maxVisible;
    const visibleRecords = isExpanded ? recordValues : recordValues.slice(0, maxVisible);
    const hiddenCount = recordValues.length - maxVisible;
    
    // Determine if we're still loading these records
    const isLoadingField = linkedTableId && loadingViewLinkedRecords[`${linkedTableId}_loading`];
    
    // Check if values are record IDs (starts with 'rec')
    const hasRecordIds = recordValues.some(v => typeof v === 'string' && v.startsWith('rec'));
    
    // Render "Show more" / "Show less" button
    const renderShowMoreButton = () => {
      if (!hasMoreRecords) return null;
      return (
        <button
          className="text-sm text-primary hover:underline font-medium"
          onClick={() => setExpandedLinkedFields(prev => ({ ...prev, [fieldName]: !isExpanded }))}
        >
          {isExpanded ? 'Show less' : `Show ${hiddenCount} more`}
        </button>
      );
    };
    
    // Get display fields for a cached record using configured preview_fields
    const getPreviewDisplayFields = (cachedRecord) => {
      const fields = cachedRecord.fields || {};
      const tableFields = cachedRecord.tableFields || [];
      const displayFields = [];
      
      // Helper to format a value for display
      const formatDisplayValue = (value, fieldName) => {
        if (value === null || value === undefined) return null;
        
        // If it's an array of record IDs, show count or clean text
        if (Array.isArray(value)) {
          // Check if they're record IDs
          if (value.length > 0 && typeof value[0] === 'string' && value[0].startsWith('rec')) {
            return `${value.length} linked record${value.length !== 1 ? 's' : ''}`;
          }
          // Check if attachments
          if (value.length > 0 && typeof value[0] === 'object' && value[0].url) {
            return `${value.length} file${value.length !== 1 ? 's' : ''}`;
          }
          return value.join(', ');
        }
        
        // If it's a single record ID, indicate it
        if (typeof value === 'string' && value.startsWith('rec') && value.length > 15) {
          return '1 linked record';
        }
        
        return value;
      };
      
      // If preview_fields are configured, use them
      if (previewFields.length > 0) {
        for (const name of previewFields) {
          if (fields[name] !== undefined && fields[name] !== null) {
            const formattedValue = formatDisplayValue(fields[name], name);
            if (formattedValue !== null) {
              displayFields.push({ name, value: formattedValue });
            }
          }
        }
        return displayFields;
      }
      
      // Fallback: Try to get specific fields for display, or fall back to first 3 fields
      const priorityFieldNames = ['Note Subject', 'Kaimahi Name', 'Note Text', 'Subject', 'Name', 'Title', 'Description', 'Notes'];
      
      // First, try to add priority fields if they exist
      for (const name of priorityFieldNames) {
        if (fields[name] !== undefined && fields[name] !== null && displayFields.length < 3) {
          const formattedValue = formatDisplayValue(fields[name], name);
          if (formattedValue !== null) {
            displayFields.push({ name, value: formattedValue });
          }
        }
      }
      
      // If we don't have 3 fields yet, add other fields
      if (displayFields.length < 3) {
        for (const fieldDef of tableFields) {
          if (!displayFields.some(f => f.name === fieldDef.name) && 
              fields[fieldDef.name] !== undefined && 
              fields[fieldDef.name] !== null &&
              displayFields.length < 3) {
            const formattedValue = formatDisplayValue(fields[fieldDef.name], fieldDef.name);
            if (formattedValue !== null) {
              displayFields.push({ name: fieldDef.name, value: formattedValue });
            }
          }
        }
      }
      
      return displayFields;
    };
    
    if (recordValues.length === 0) {
      return (
        <span className="text-muted-foreground">No linked records</span>
      );
    }
    
    // If we have record IDs and a linked table, try to show rich cards
    if (hasRecordIds && linkedTableId) {
      return (
        <div className="space-y-2">
          {visibleRecords.map((recordIdOrName, idx) => {
            // Try to get cached record data from state
            const recordId = typeof recordIdOrName === 'string' && recordIdOrName.startsWith('rec') 
              ? recordIdOrName 
              : null;
            const cachedRecord = recordId ? viewLinkedRecordsCache[recordId] : null;
            
            if (cachedRecord) {
              // Get display fields using configured preview_fields
              const displayFields = getPreviewDisplayFields(cachedRecord);
              
              // If expand is not allowed, render as simple non-clickable tag
              if (!allowExpand) {
                return (
                  <div
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted/50 border border-border text-sm text-foreground mr-2 mb-1"
                    data-testid={`linked-record-tag-${recordId}`}
                  >
                    {displayFields.length > 0 
                      ? cleanLinkedRecordValue(String(displayFields[0].value))
                      : cleanLinkedRecordValue(String(recordIdOrName))}
                  </div>
                );
              }
              
              // Render as rich expandable card
              return (
                <div
                  key={idx}
                  className="border border-border rounded-lg p-3 bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer group"
                  onClick={() => handleExpandLinkedRecord(recordId, linkedTableId, fieldName)}
                  data-testid={`linked-record-card-${recordId}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-2 min-w-0">
                      {displayFields.map((field, fieldIdx) => (
                        <div key={fieldIdx} className="min-w-0">
                          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                            {field.name}
                          </span>
                          <p className={`text-sm text-foreground ${fieldIdx === 0 ? 'font-semibold' : ''} ${field.name.toLowerCase().includes('text') || field.name.toLowerCase().includes('notes') || field.name.toLowerCase().includes('description') ? 'line-clamp-2' : 'truncate'}`}>
                            {typeof field.value === 'string' ? cleanLinkedRecordValue(field.value) : String(field.value)}
                          </p>
                        </div>
                      ))}
                    </div>
                    <button
                      className="flex-shrink-0 p-1.5 rounded-md bg-primary/10 text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpandLinkedRecord(recordId, linkedTableId, fieldName);
                      }}
                      title="View full details"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            } else {
              // Record not cached yet - show loading placeholder or raw value
              const displayValue = cleanLinkedRecordValue(String(recordIdOrName));
              
              // If expand is not allowed, render as simple non-clickable tag
              if (!allowExpand) {
                return (
                  <div
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted/50 border border-border text-sm text-foreground mr-2 mb-1"
                  >
                    {isLoadingField && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground mr-1.5" />}
                    {displayValue}
                  </div>
                );
              }
              
              return (
                <div
                  key={idx}
                  className="border border-border rounded-lg p-3 bg-muted/30 flex items-center gap-2"
                >
                  {isLoadingField && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  <span className="text-sm text-foreground">{displayValue}</span>
                </div>
              );
            }
          })}
          {/* Show more/less button */}
          {renderShowMoreButton()}
        </div>
      );
    }
    
    // Fallback: render for non-ID values like "535086"
    // These are display values from cellFormat=string
    return (
      <div className="space-y-2">
        {visibleRecords.map((item, idx) => {
          const displayValue = cleanLinkedRecordValue(String(item));
          
          // If expand is not allowed, render as simple non-clickable tag
          if (!allowExpand) {
            return (
              <div
                key={idx}
                className="inline-flex items-center px-2.5 py-1 rounded-md bg-muted/50 border border-border text-sm text-foreground mr-2 mb-1"
              >
                {displayValue}
              </div>
            );
          }
          
          // Render as clickable card that can be expanded
          return (
            <div
              key={idx}
              className="border border-border rounded-lg p-3 bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer group"
              onClick={() => handleSearchAndExpandLinkedRecord(item, linkedTableId, fieldName)}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-foreground font-medium">{displayValue}</span>
                <button
                  className="flex-shrink-0 p-1.5 rounded-md bg-primary/10 text-primary opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSearchAndExpandLinkedRecord(item, linkedTableId, fieldName);
                  }}
                  title="View full details"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
        {/* Show more/less button */}
        {renderShowMoreButton()}
      </div>
    );
  };

  // Search for a linked record by its display value and expand it
  const handleSearchAndExpandLinkedRecord = async (displayValue, linkedTableId, fieldName) => {
    if (!linkedTableId || !displayValue) return;
    
    const linkedTable = fullSchema?.tables?.find(t => t.id === linkedTableId);
    if (!linkedTable) {
      // Try to fetch schema first
      try {
        const response = await axios.get(`${API}/airtable/schema?portal_id=${portalId}`, authHeaders);
        const schema = response.data;
        setFullSchema(schema);
        const table = schema?.tables?.find(t => t.id === linkedTableId);
        if (table) {
          await searchAndExpandRecord(displayValue, table);
        }
      } catch (error) {
        toast.error("Failed to load linked record details");
      }
      return;
    }
    
    await searchAndExpandRecord(displayValue, linkedTable);
  };

  // Search for record and show expanded view
  const searchAndExpandRecord = async (searchValue, linkedTable) => {
    setLoadingExpandedRecord(true);
    setExpandedLinkedDialogOpen(true);
    
    try {
      const searchStr = String(searchValue).trim();
      
      // Try to use Airtable formula to search by primary field
      // First, get the primary field name (first field in the table schema)
      const primaryFieldName = linkedTable.fields?.[0]?.name || '';
      
      // Build filter formula to search exact match or contains
      const formula = primaryFieldName 
        ? `OR({${primaryFieldName}}="${searchStr}", FIND("${searchStr}", {${primaryFieldName}}) > 0)`
        : '';
      
      // Search the linked table for a record matching this value
      const response = await axios.post(`${API}/airtable/records?portal_id=${portalId}`, {
        table_name: linkedTable.name,
        page_size: 100,
        formula: formula || undefined
      }, authHeaders);
      
      const records = response.data.records || [];
      
      // If formula search returned results, use the first match
      if (records.length > 0) {
        setExpandedLinkedRecord({
          ...records[0],
          tableName: linkedTable.name,
          tableFields: linkedTable.fields
        });
        return;
      }
      
      // Fallback: search without formula (if formula didn't work)
      const fallbackResponse = await axios.post(`${API}/airtable/records?portal_id=${portalId}`, {
        table_name: linkedTable.name,
        page_size: 100
      }, authHeaders);
      
      const fallbackRecords = fallbackResponse.data.records || [];
      const searchLower = searchStr.toLowerCase();
      
      // Find record where any field matches the display value
      const matchedRecord = fallbackRecords.find(rec => {
        const fields = rec.fields || {};
        return Object.values(fields).some(val => 
          String(val).toLowerCase().trim() === searchLower ||
          String(val).toLowerCase().includes(searchLower)
        );
      });
      
      if (matchedRecord) {
        setExpandedLinkedRecord({
          ...matchedRecord,
          tableName: linkedTable.name,
          tableFields: linkedTable.fields
        });
      } else {
        toast.error("Could not find linked record details");
        setExpandedLinkedDialogOpen(false);
      }
    } catch (error) {
      console.error("Failed to search linked record:", error);
      toast.error("Failed to load record details");
      setExpandedLinkedDialogOpen(false);
    } finally {
      setLoadingExpandedRecord(false);
    }
  };

  // Format value for detail view (more detailed)
  const formatDetailValue = (value, fieldName) => {
    if (value === null || value === undefined) return <span className="text-muted-foreground">-</span>;
    
    // Get field type for date formatting
    const fieldType = getFieldSchema(fieldName)?.type;
    
    // Handle date fields - format as dd/mm/yyyy
    if (fieldType === 'date' && typeof value === 'string') {
      return <span className="text-foreground">{formatDate(value)}</span>;
    }
    
    // Handle datetime fields - format as dd/mm/yyyy hh:mm
    if (fieldType === 'dateTime' && typeof value === 'string') {
      return <span className="text-foreground">{formatDateTime(value)}</span>;
    }
    
    // Handle attachments/files - use thumbnail display
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0].url) {
      // This is an attachment array - render as thumbnails
      return renderAttachmentThumbnails(value, fieldName, false);
    }
    
    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <span key={idx} className="px-2 py-1 bg-secondary rounded text-sm">
              {cleanLinkedRecordValue(String(item))}
            </span>
          ))}
        </div>
      );
    }
    
    if (typeof value === "object") {
      // Single attachment object
      if (value.url && (value.filename || value.type)) {
        return renderAttachmentThumbnails([value], fieldName, false);
      }
      if (value.url) {
        return (
          <a href={value.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            {value.filename || value.url}
          </a>
        );
      }
      return <pre className="text-sm bg-secondary/50 p-2 rounded overflow-auto">{JSON.stringify(value, null, 2)}</pre>;
    }
    
    if (typeof value === "boolean") {
      return (
        <span className={`px-2 py-1 rounded text-sm ${value ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
          {value ? "Yes" : "No"}
        </span>
      );
    }
    
    // Clean up linked record format for string values
    const cleanedValue = cleanLinkedRecordValue(String(value));
    
    // Check if it's a URL
    if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
          {value}
        </a>
      );
    }
    
    return <span className="text-foreground">{cleanedValue}</span>;
  };

  // Get primary display value for a record (first field or specific fields)
  const getRecordTitle = (record) => {
    const fields = record.fields || {};
    // Try common title fields
    const titleFields = ['Name', 'Title', 'Full Name', 'name', 'title', 'Subject', 'Email'];
    for (const field of titleFields) {
      if (fields[field]) return formatValue(fields[field]);
    }
    // Fall back to first field
    const firstKey = Object.keys(fields)[0];
    return firstKey ? formatValue(fields[firstKey]) : 'Record';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-0'} h-screen sticky top-0 bg-card border-r border-border flex-shrink-0 transition-all duration-300 overflow-hidden`}
      >
        <div className="w-64 h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-border" style={{ background: isPublicView ? `linear-gradient(to right, ${primaryColor}15, transparent)` : undefined }}>
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-5 h-5 rounded flex items-center justify-center"
                style={{ backgroundColor: isPublicView ? primaryColor : undefined }}
              >
                <Database className="w-3 h-3" style={{ color: isPublicView ? 'white' : undefined }} />
              </div>
              <span className="font-bold text-foreground truncate">{portal?.branding?.company_name || portal?.name}</span>
            </div>
            <p className="text-xs text-muted-foreground truncate">{portal?.description}</p>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-2 overflow-y-auto">
            <p className="text-xs uppercase tracking-wider text-muted-foreground px-3 py-2">Pages</p>
            {pages.length === 0 ? (
              <p className="text-sm text-muted-foreground px-3 py-2">No pages yet</p>
            ) : (
              <ul className="space-y-1">
                {pages.map((page) => (
                  <li key={page.page_id}>
                    <button
                      onClick={() => handlePageChange(page)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        activePage?.page_id === page.page_id
                          ? 'font-medium'
                          : 'text-foreground hover:bg-muted'
                      }`}
                      style={activePage?.page_id === page.page_id ? { 
                        backgroundColor: `${primaryColor}15`, 
                        color: primaryColor 
                      } : {}}
                      data-testid={`nav-page-${page.page_id}`}
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{page.nav_title || page.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-border">
            {isPublicView ? (
              // Public view: show user name and role
              portalUser && (
                <div className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {(portalUser.name || portalUser.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {portalUser.name || portalUser.email}
                    </p>
                    {portalUser.role && (
                      <p className="text-xs text-muted-foreground truncate">
                        {portalUser.role}
                      </p>
                    )}
                  </div>
                </div>
              )
            ) : (
              // Admin view: show settings button
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => navigate(`/portal/${portalId}/config`)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Portal Settings
              </Button>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="glass border-b border-border sticky top-0 z-40">
          <div className="px-4 md:px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="toggle-sidebar"
              >
                {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              {!isPublicView && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => navigate("/dashboard")}
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              <div>
                <h1 className="text-lg font-bold text-foreground">
                  {activePage?.nav_title || activePage?.name || "Select a page"}
                </h1>
                {activePage && !isPublicView && (
                  <p className="text-xs text-muted-foreground">Table: {activePage.table_name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-foreground"
                onClick={handleRefresh}
                disabled={recordsLoading || !activePage}
                data-testid="refresh-btn"
                title="Refresh data"
              >
                <RefreshCw className={`w-5 h-5 ${recordsLoading ? 'animate-spin' : ''}`} />
              </Button>
              <ThemeToggle />
              {isPublicView ? (
                // Public view: show user info and logout
                portalUser && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={onLogout}
                    title="Sign out"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                )
              ) : (
                // Admin view: show configure button
                <Button
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => navigate(`/portal/${portalId}/config`)}
                  data-testid="config-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {!activePage ? (
            <div className="text-center py-24">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">No page selected</h3>
              <p className="text-muted-foreground mb-6">
                {pages.length === 0 
                  ? "Add pages to this portal to view data"
                  : "Select a page from the sidebar to view data"
                }
              </p>
              {pages.length === 0 && !isPublicView && (
                <Button
                  className="btn-primary"
                  onClick={() => navigate(`/portal/${portalId}/config`)}
                >
                  Add Pages
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground"
                      style={{ borderColor: searchTerm && isPublicView ? primaryColor : undefined }}
                      placeholder="Search client name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="search-input"
                    />
                    {isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* New Record Button - only show if allow_create is enabled */}
                    {activePage?.allow_create && (
                      <Button
                        variant="default"
                        className="gap-2"
                        style={isPublicView ? { backgroundColor: primaryColor } : {}}
                        onClick={handleOpenCreateDialog}
                        data-testid="new-record-btn"
                      >
                        <Plus className="w-4 h-4" />
                        <span>New Record</span>
                      </Button>
                    )}
                    {/* Filter Button */}
                    {getFilterableFields.length > 0 && (
                      <Button
                        variant="outline"
                        className={`gap-2 ${activeFilterCount > 0 ? '' : 'btn-secondary'}`}
                        style={activeFilterCount > 0 ? { borderColor: primaryColor, color: primaryColor } : {}}
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                        data-testid="filters-toggle-btn"
                      >
                        <Filter className="w-4 h-4" />
                        <span>Filters</span>
                        {activeFilterCount > 0 && (
                          <span 
                            className="text-white text-xs px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: primaryColor }}
                          >
                            {activeFilterCount}
                          </span>
                        )}
                        {filtersExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      {loadingAllRecords && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      {filteredRecords.length} records
                      {loadingAllRecords && " (loading all...)"}
                      {!loadingAllRecords && hasMore && !filtersExpanded && " (more available)"}
                      {!loadingAllRecords && allRecordsLoaded && " (all loaded)"}
                      {searchTerm && ` matching "${searchTerm}"`}
                      {activeFilterCount > 0 && ` (filtered)`}
                    </span>
                  </div>
                </div>

                {/* Expandable Filter Dropdowns */}
                {filtersExpanded && getFilterableFields.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4" data-testid="filters-panel">
                    {loadingAllRecords && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3 pb-3 border-b border-border">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Loading all records for filtering...</span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-4 items-end">
                      {getFilterableFields.map((field) => {
                        const options = getFilterOptions(field);
                        const selectedValues = activeFilters[field.name] || [];
                        
                        return (
                          <div key={field.name} className="min-w-[180px]">
                            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                              {field.name}
                            </label>
                            <Select
                              value={selectedValues.length === 1 ? selectedValues[0] : selectedValues.length > 1 ? "__multiple__" : "__all__"}
                              onValueChange={(value) => {
                                if (value === "__all__") {
                                  // Clear this filter
                                  setActiveFilters(prev => {
                                    const { [field.name]: _, ...rest } = prev;
                                    return rest;
                                  });

                                } else if (value === "__multiple__") {
                                  // Do nothing, this is just a display state
                                } else {
                                  // Set single value
                                  setActiveFilters(prev => ({
                                    ...prev,
                                    [field.name]: [value]
                                  }));
                                }
                              }}
                            >
                              <SelectTrigger 
                                className={`bg-background border-border text-foreground h-9 ${selectedValues.length > 0 ? 'border-cyan-500' : ''}`}
                                data-testid={`filter-dropdown-${field.name}`}
                              >
                                <SelectValue>
                                  {selectedValues.length === 0 
                                    ? "All" 
                                    : selectedValues.length === 1 
                                      ? selectedValues[0]
                                      : `${selectedValues.length} selected`}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border max-h-64">
                                <SelectItem value="__all__" className="text-foreground">
                                  All
                                </SelectItem>
                                {options.map((option) => (
                                  <SelectItem
                                    key={option}
                                    value={option}
                                    className="text-foreground"
                                  >
                                    <div className="flex items-center gap-2">
                                      {selectedValues.includes(option) && (
                                        <Check className="w-3 h-3 text-cyan-500" />
                                      )}
                                      <span>{option}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* Multi-select tags below dropdown */}
                            {selectedValues.length > 1 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {selectedValues.map(val => (
                                  <span 
                                    key={val}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-cyan-500/20 text-cyan-500 rounded-full cursor-pointer hover:bg-cyan-500/30"
                                    onClick={() => toggleFilterValue(field.name, val)}
                                  >
                                    {val.length > 15 ? val.substring(0, 15) + '...' : val}
                                    <X className="w-3 h-3" />
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Clear All Button */}
                      {activeFilterCount > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground h-9"
                          onClick={clearAllFilters}
                          data-testid="clear-filters-btn"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Table */}
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="records-table">
                    <thead>
                      <tr style={{ backgroundColor: isPublicView ? `${primaryColor}15` : '#3CC79220' }}>
                        <th className="px-4 py-3 text-left text-xs font-bold text-foreground uppercase tracking-wider w-24">
                          Actions
                        </th>
                        {fields.map((field) => (
                          <th
                            key={field}
                            className="px-4 py-3 text-left text-xs font-bold text-foreground uppercase tracking-wider cursor-pointer hover:opacity-80 transition-colors"
                            onClick={() => handleSort(field)}
                          >
                            <div className="flex items-center gap-2">
                              <span>{field}</span>
                              {sortField === field && (
                                <ArrowUpDown className={`w-3 h-3 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {recordsLoading && records.length === 0 ? (
                        <tr>
                          <td colSpan={(fields.length || 0) + 1} className="px-4 py-12 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: primaryColor }} />
                          </td>
                        </tr>
                      ) : filteredRecords.length === 0 ? (
                        <tr>
                          <td colSpan={(fields.length || 0) + 1} className="px-4 py-12 text-center text-muted-foreground">
                            {searchTerm ? `No clients found matching "${searchTerm}"` : "No records in this table"}
                          </td>
                        </tr>
                      ) : (
                        filteredRecords.map((record) => (
                          <tr
                            key={record.id}
                            className="border-t border-border/50 hover:bg-muted/30 transition-colors"
                            data-testid={`record-${record.id}`}
                          >
                            <td className="px-4 py-3">
                              <Button
                                variant="outline"
                                size="sm"
                                className="hover:bg-opacity-10"
                                style={{ 
                                  color: primaryColor, 
                                  borderColor: `${primaryColor}50`,
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${primaryColor}15`}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                onClick={() => openRecordDetail(record)}
                                data-testid={`open-record-${record.id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Open
                              </Button>
                            </td>
                            {fields.map((field) => {
                              const fieldDef = tableSchema?.fields?.find(f => f.name === field);
                              return (
                                <td key={field} className="px-4 py-3 text-sm text-foreground max-w-xs truncate">
                                  {formatValue(record.fields?.[field], field, fieldDef?.type)}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="mt-6 text-center">
                  <Button
                    variant="outline"
                    className="btn-secondary"
                    onClick={handleLoadMore}
                    disabled={recordsLoading}
                    data-testid="load-more-btn"
                  >
                    {recordsLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mr-2" />
                    )}
                    Load More Records
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Record Detail/Edit Dialog */}
      <Dialog open={recordDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsEditing(false);
        }
        setRecordDialogOpen(open);
      }}>
        <DialogContent 
          className="bg-card border-border text-foreground max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          key={`record-dialog-${Object.keys(viewLinkedRecordsCache).length}`}
        >
          <DialogHeader className="border-b border-border pb-4">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="text-lg">
                    {selectedRecord ? getRecordTitle(selectedRecord) : 'Record Details'}
                  </span>
                  <p className="text-sm font-normal text-muted-foreground mt-1">
                    {activePage?.nav_title || activePage?.name}
                  </p>
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {selectedRecord && (
              <div className="space-y-4">
                {/* Use visible_fields order if configured, otherwise fall back to tableSchema or record fields */}
                {(() => {
                  // Get the ordered list of fields to display
                  let fieldsToDisplay = [];
                  
                  if (activePage?.visible_fields?.length > 0) {
                    // Use configured visible_fields order
                    fieldsToDisplay = activePage.visible_fields;
                  } else if (tableSchema?.fields) {
                    // Fall back to schema order
                    fieldsToDisplay = tableSchema.fields.map(f => f.name);
                  } else {
                    // Last resort: use record's field keys
                    fieldsToDisplay = Object.keys(selectedRecord.fields || {});
                  }
                  
                  return fieldsToDisplay.map((fieldName) => {
                    const value = selectedRecord.fields?.[fieldName];
                    const fieldType = getFieldSchema(fieldName)?.type;
                    const linkedTableId = getFieldSchema(fieldName)?.options?.linkedTableId;
                    const editable = isFieldEditable(fieldName);
                    const isCurrentlyEditing = editingFieldName === fieldName;
                    const isLinkedRecordField = fieldType === 'multipleRecordLinks';
                    const allowCreateView = activePage?.field_config?.[fieldName]?.allow_create_view === true;
                    const hasValue = value !== null && value !== undefined && value !== '';
                    
                    return (
                      <div key={fieldName} className="border-b border-border/50 pb-4 last:border-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <label className="text-sm font-bold text-emerald-500 tracking-wide">
                              {fieldName}
                            </label>
                            {/* Edit pencil - positioned close to field name */}
                            {editable && !isCurrentlyEditing && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 text-muted-foreground hover:text-yellow-500 ml-0"
                                onClick={() => startInlineEdit(fieldName)}
                                title={`Edit ${fieldName}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                            {/* CREATE NEW button next to field name for linked records */}
                            {isLinkedRecordField && allowCreateView && linkedTableId && !isCurrentlyEditing && (
                              <Button
                                size="sm"
                                className="h-7 px-3 text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow-md transition-all ml-2"
                                onClick={() => handleOpenCreateLinkedDialog(fieldName, linkedTableId)}
                                data-testid={`create-linked-record-btn-${fieldName}`}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" />
                                CREATE NEW
                              </Button>
                            )}
                          </div>
                        </div>
                        {isCurrentlyEditing ? (
                          <div className="space-y-2">
                            {renderFieldInput(
                              fieldName, 
                              editedFieldValue, 
                              (newValue) => setEditedFieldValue(newValue)
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <Button
                                size="sm"
                                className="btn-primary h-7 px-3"
                                onClick={saveInlineEdit}
                                disabled={isSaving}
                              >
                              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-3"
                              onClick={cancelInlineEdit}
                              disabled={isSaving}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-foreground">
                          {!hasValue ? (
                            <span className="text-muted-foreground italic">No value</span>
                          ) : fieldType === 'multipleRecordLinks' ? (
                            renderLinkedRecordsView(value, fieldName, linkedTableId)
                          ) : fieldType === 'multipleAttachments' ? (
                            renderAttachmentsForField(value, fieldName)
                          ) : (
                            formatDetailValue(value, fieldName)
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4 flex justify-end">
              <Button
                className="btn-primary"
                onClick={async () => {
                  // Save any pending changes, then close
                  await handleSaveRecord();
                  setRecordDialogOpen(false);
                  setEditingFieldName(null);
                  setEditedFieldValue(null);
                }}
                disabled={isSaving}
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save & Exit
              </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Record Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => {
        setCreateDialogOpen(open);
        if (!open) {
          setCreateFormPendingFiles({});
          setNewRecordFields({});
        }
      }}>
        <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border pb-4">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="text-lg">Create New Record</span>
                <p className="text-sm font-normal text-muted-foreground mt-1">
                  {activePage?.nav_title || activePage?.name} - {activePage?.table_name}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {tableSchema ? (
              <div className="space-y-4">
                {(() => {
                  const readOnlyTypes = ['autoNumber', 'formula', 'rollup', 'lookup', 'count', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy'];
                  const allFields = tableSchema.fields?.filter(f => !readOnlyTypes.includes(f.type)) || [];
                  
                  // Check if create_form_config is configured
                  const createFormConfig = activePage?.create_form_config;
                  const hasConfig = createFormConfig?.visible_fields?.length > 0;
                  const fieldSettings = createFormConfig?.field_settings || {};
                  
                  // Get fields in configured order, or all fields if not configured
                  let fieldsToShow;
                  if (hasConfig) {
                    const fieldOrder = createFormConfig.field_order || createFormConfig.visible_fields;
                    fieldsToShow = fieldOrder
                      .map(fieldName => allFields.find(f => f.name === fieldName))
                      .filter(Boolean);
                  } else {
                    fieldsToShow = allFields;
                  }
                  
                  return fieldsToShow.map((field) => {
                    const isRequired = fieldSettings[field.name]?.required;
                    return (
                      <div key={field.id} className="border-b border-border/50 pb-4 last:border-0">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-2">
                          {field.name}
                          {isRequired && <span className="text-red-500 ml-1">*</span>}
                          <span className="ml-2 text-xs normal-case opacity-60">({field.type})</span>
                        </label>
                        {renderFieldInput(
                          field.name,
                          newRecordFields[field.name],
                          (newValue) => setNewRecordFields(prev => ({ ...prev, [field.name]: newValue })),
                          true
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4 flex justify-between">
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={() => setCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleCreateRecord}
              disabled={isCreating || !tableSchema}
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Create Record
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Linked Record Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-lg max-h-[70vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border pb-4">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <span className="text-lg">Record Preview</span>
                {previewRecord && (
                  <p className="text-sm font-normal text-muted-foreground mt-1">
                    {previewRecord.tableName}
                  </p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            ) : previewRecord ? (
              <div className="space-y-4">
                {/* Show first 3 fields prominently */}
                {previewRecord.tableFields?.slice(0, 3).map((field) => (
                  <div key={field.id} className="border-b border-border/50 pb-3 last:border-0">
                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                      {field.name}
                    </label>
                    <div className="text-foreground">
                      {formatDetailValue(previewRecord.fields?.[field.name], field.name)}
                    </div>
                  </div>
                ))}
                
                {/* Show remaining fields in a collapsed section */}
                {previewRecord.tableFields?.length > 3 && (
                  <details className="mt-4">
                    <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                      Show all fields ({previewRecord.tableFields.length - 3} more)
                    </summary>
                    <div className="mt-4 space-y-4">
                      {previewRecord.tableFields?.slice(3).map((field) => (
                        <div key={field.id} className="border-b border-border/50 pb-3 last:border-0">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                            {field.name}
                          </label>
                          <div className="text-foreground text-sm">
                            {formatDetailValue(previewRecord.fields?.[field.name], field.name)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No record data available
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4 flex justify-end">
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={() => setPreviewDialogOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create New Linked Record Dialog */}
      <Dialog open={createLinkedDialogOpen} onOpenChange={setCreateLinkedDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border pb-4">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <span className="text-lg">Create New Linked Record</span>
                {getLinkedTableSchema() && (
                  <p className="text-sm font-normal text-muted-foreground mt-1">
                    Adding to: {getLinkedTableSchema()?.name}
                  </p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {getLinkedTableSchema() ? (
              <div className="space-y-4">
                {(() => {
                  // Get create_config for the parent field if available
                  const createConfig = createLinkedFieldName 
                    ? activePage?.field_config?.[createLinkedFieldName]?.create_config 
                    : null;
                  
                  // Get all editable fields (exclude auto-generated fields but allow linked records)
                  const allFields = getLinkedTableSchema().fields
                    ?.filter(f => !['autoNumber', 'formula', 'rollup', 'lookup', 'count', 'createdTime', 'lastModifiedTime', 'createdBy', 'lastModifiedBy'].includes(f.type)) || [];
                  
                  // Determine which fields to show and in what order
                  let fieldsToDisplay = allFields;
                  
                  if (createConfig?.field_order?.length > 0) {
                    // Use configured field order and visibility
                    const visibleSet = new Set(createConfig.visible_fields || []);
                    fieldsToDisplay = createConfig.field_order
                      .filter(fieldName => visibleSet.has(fieldName))
                      .map(fieldName => allFields.find(f => f.name === fieldName))
                      .filter(Boolean);
                  } else {
                    // Fallback: limit to first 10 non-linked fields for simplicity
                    fieldsToDisplay = allFields.filter(f => f.type !== 'multipleRecordLinks').slice(0, 10);
                  }
                  
                  // Helper to check if a field is required
                  const isFieldRequired = (fieldName) => {
                    return createConfig?.field_settings?.[fieldName]?.required === true;
                  };
                  
                  return fieldsToDisplay.map((field) => {
                    const required = isFieldRequired(field.name);
                    return (
                    <div key={field.id || field.name} className="border-b border-border/50 pb-4 last:border-0">
                      <label className="text-sm font-bold text-emerald-500 tracking-wide block mb-2">
                        {field.name}
                        {required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.type === 'multilineText' ? (
                        <Textarea
                          className={`bg-background border-border text-foreground ${required && !newLinkedRecordFields[field.name] ? 'border-red-300' : ''}`}
                          value={newLinkedRecordFields[field.name] || ''}
                          onChange={(e) => setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: e.target.value || null }))}
                          rows={2}
                          placeholder={`Enter ${field.name}`}
                        />
                      ) : field.type === 'singleSelect' ? (
                        <Select 
                          value={newLinkedRecordFields[field.name] || '__none__'} 
                          onValueChange={(v) => setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: v === '__none__' ? null : v }))}
                        >
                          <SelectTrigger className="bg-background border-border text-foreground">
                            <SelectValue placeholder={`Select ${field.name}`} />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            <SelectItem value="__none__">
                              <span className="text-muted-foreground">None</span>
                            </SelectItem>
                            {field.options?.choices?.map((choice) => (
                              <SelectItem key={choice.id || choice.name} value={choice.name}>
                                {choice.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.type === 'multipleSelects' ? (
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {(newLinkedRecordFields[field.name] || []).map((selected, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm"
                              >
                                {selected}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newValues = (newLinkedRecordFields[field.name] || []).filter((_, i) => i !== idx);
                                    setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: newValues.length > 0 ? newValues : null }));
                                  }}
                                  className="hover:text-destructive"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <Select
                            value="__select__"
                            onValueChange={(v) => {
                              if (v !== '__select__') {
                                const currentValues = newLinkedRecordFields[field.name] || [];
                                if (!currentValues.includes(v)) {
                                  setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: [...currentValues, v] }));
                                }
                              }
                            }}
                          >
                            <SelectTrigger className="bg-background border-border text-foreground">
                              <SelectValue placeholder={`Add ${field.name}...`} />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="__select__">
                                <span className="text-muted-foreground">Select to add...</span>
                              </SelectItem>
                              {field.options?.choices?.filter(choice => 
                                !(newLinkedRecordFields[field.name] || []).includes(choice.name)
                              ).map((choice) => (
                                <SelectItem key={choice.id || choice.name} value={choice.name}>
                                  {choice.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : field.type === 'checkbox' ? (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={!!newLinkedRecordFields[field.name]}
                            onCheckedChange={(checked) => setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: checked }))}
                            className="border-border data-[state=checked]:bg-primary"
                          />
                          <span className="text-sm text-muted-foreground">
                            {newLinkedRecordFields[field.name] ? 'Yes' : 'No'}
                          </span>
                        </div>
                      ) : field.type === 'number' || field.type === 'currency' || field.type === 'percent' ? (
                        <Input
                          type="number"
                          className="bg-background border-border text-foreground"
                          value={newLinkedRecordFields[field.name] || ''}
                          onChange={(e) => setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: e.target.value ? Number(e.target.value) : null }))}
                          placeholder={`Enter ${field.name}`}
                        />
                      ) : field.type === 'date' ? (
                        <Input
                          type="date"
                          className="bg-background border-border text-foreground"
                          value={newLinkedRecordFields[field.name] || ''}
                          onChange={(e) => setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: e.target.value || null }))}
                        />
                      ) : field.type === 'multipleRecordLinks' ? (
                        (() => {
                          const linkedTableId = field.options?.linkedTableId;
                          const selectedRecords = newLinkedRecordFields[field.name] || [];
                          const cachedRecords = createFormLinkedCache[linkedTableId] || [];
                          const isLoading = createFormLinkedLoading[linkedTableId];
                          const searchTerm = createFormLinkedSearch[field.name] || '';
                          
                          // Check if this is an auto-linked field (linked back to parent record)
                          const autoLinkedName = newLinkedRecordFields[`__autoLinked_${field.name}`];
                          const isAutoLinked = autoLinkedName && selectedRecords.length > 0;
                          
                          // Get display name for a record ID
                          const getRecordName = (recordId) => {
                            // First check if we have the auto-linked name
                            if (autoLinkedName && selectedRecords.includes(recordId)) {
                              return autoLinkedName;
                            }
                            const rec = cachedRecords.find(r => r.id === recordId);
                            return rec?.name || recordId;
                          };
                          
                          // If auto-linked, show as read-only
                          if (isAutoLinked) {
                            return (
                              <div className="flex items-center gap-2 p-3 bg-muted/50 border border-border rounded-md">
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 rounded-md">
                                  <span className="text-sm font-medium">{autoLinkedName}</span>
                                  <span className="text-xs opacity-70">(auto-linked)</span>
                                </div>
                              </div>
                            );
                          }
                          
                          // Filter available records
                          const availableRecords = cachedRecords.filter(rec => {
                            const isSelected = selectedRecords.includes(rec.id);
                            const matchesSearch = !searchTerm || 
                              String(rec.name || '').toLowerCase().includes(searchTerm.toLowerCase());
                            return !isSelected && matchesSearch;
                          });
                          
                          return (
                            <div className="space-y-2">
                              {/* Selected records as tags */}
                              <div className="flex flex-wrap gap-2 min-h-[36px] p-2 bg-background border border-border rounded-md">
                                {selectedRecords.length === 0 ? (
                                  <span className="text-muted-foreground text-sm">No records selected</span>
                                ) : (
                                  selectedRecords.map((recordId, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-500 rounded text-sm"
                                    >
                                      {getRecordName(recordId)}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newValues = selectedRecords.filter((_, i) => i !== idx);
                                          setNewLinkedRecordFields(prev => ({ 
                                            ...prev, 
                                            [field.name]: newValues.length > 0 ? newValues : null 
                                          }));
                                        }}
                                        className="hover:text-destructive ml-1"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))
                                )}
                              </div>
                              
                              {/* Search input */}
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                  className="bg-background border-border text-foreground pl-9"
                                  placeholder={`Search ${field.name}...`}
                                  value={searchTerm}
                                  onChange={(e) => setCreateFormLinkedSearch(prev => ({ 
                                    ...prev, 
                                    [field.name]: e.target.value 
                                  }))}
                                  onFocus={() => {
                                    if (!cachedRecords.length && !isLoading) {
                                      fetchCreateFormLinkedRecords(linkedTableId, field.name);
                                    }
                                  }}
                                />
                              </div>
                              
                              {/* Results dropdown */}
                              {(isLoading || cachedRecords.length > 0) && (
                                <div className="max-h-[200px] overflow-y-auto border border-border rounded-md bg-card">
                                  {isLoading ? (
                                    <div className="flex items-center justify-center py-4">
                                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                                      <span className="ml-2 text-sm text-muted-foreground">Loading records...</span>
                                    </div>
                                  ) : availableRecords.length === 0 ? (
                                    <div className="py-3 px-4 text-sm text-muted-foreground text-center">
                                      {searchTerm ? 'No matching records' : 'No available records'}
                                    </div>
                                  ) : (
                                    availableRecords.slice(0, 20).map((rec) => (
                                      <button
                                        key={rec.id}
                                        type="button"
                                        className="w-full text-left px-4 py-2 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
                                        onClick={() => {
                                          const newValues = [...selectedRecords, rec.id];
                                          setNewLinkedRecordFields(prev => ({ 
                                            ...prev, 
                                            [field.name]: newValues 
                                          }));
                                          setCreateFormLinkedSearch(prev => ({ ...prev, [field.name]: '' }));
                                        }}
                                      >
                                        <span className="text-sm font-medium text-foreground">{rec.name}</span>
                                      </button>
                                    ))
                                  )}
                                </div>
                              )}
                              
                              {/* Click to load prompt */}
                              {!isLoading && cachedRecords.length === 0 && (
                                <button
                                  type="button"
                                  className="text-sm text-primary hover:underline"
                                  onClick={() => fetchCreateFormLinkedRecords(linkedTableId, field.name)}
                                >
                                  Click to load available records
                                </button>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <Input
                          className="bg-background border-border text-foreground"
                          value={newLinkedRecordFields[field.name] || ''}
                          onChange={(e) => setNewLinkedRecordFields(prev => ({ ...prev, [field.name]: e.target.value || null }))}
                          placeholder={`Enter ${field.name}`}
                        />
                      )}
                    </div>
                  );
                  });
                })()}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4 flex justify-between">
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={() => setCreateLinkedDialogOpen(false)}
              disabled={isCreatingLinked}
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-md hover:shadow-lg transition-all"
              onClick={handleCreateLinkedRecord}
              disabled={isCreatingLinked || !getLinkedTableSchema()}
            >
              {isCreatingLinked ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              CREATE & LINK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expanded Linked Record Dialog (for viewing linked record details) */}
      <Dialog open={expandedLinkedDialogOpen} onOpenChange={setExpandedLinkedDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b border-border pb-4">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="text-lg">
                  {expandedLinkedRecord ? (
                    expandedLinkedRecord.fields?.[expandedLinkedRecord.tableFields?.[0]?.name] || 'Record Details'
                  ) : 'Loading...'}
                </span>
                {expandedLinkedRecord && (
                  <p className="text-sm font-normal text-muted-foreground mt-1">
                    {expandedLinkedRecord.tableName}
                  </p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingExpandedRecord ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : expandedLinkedRecord ? (
              <div className="space-y-4">
                {(() => {
                  // Get expand_config for the parent field if available
                  const expandConfig = expandedLinkedParentField 
                    ? activePage?.field_config?.[expandedLinkedParentField]?.expand_config 
                    : null;
                  
                  // Determine which fields to show and in what order
                  let fieldsToDisplay = expandedLinkedRecord.tableFields || [];
                  
                  if (expandConfig?.field_order?.length > 0) {
                    // Use configured field order and visibility
                    const visibleSet = new Set(expandConfig.visible_fields || []);
                    fieldsToDisplay = expandConfig.field_order
                      .filter(fieldName => visibleSet.has(fieldName))
                      .map(fieldName => expandedLinkedRecord.tableFields?.find(f => f.name === fieldName))
                      .filter(Boolean);
                  }
                  
                  return fieldsToDisplay.map((field) => {
                    const value = expandedLinkedRecord.fields?.[field.name];
                    // Skip if field has no value
                    if (value === null || value === undefined) return null;
                    
                    // Format date fields
                    const renderFieldValue = () => {
                      if (field.type === 'multilineText') {
                        return (
                          <p className="whitespace-pre-wrap text-sm leading-relaxed bg-muted/30 rounded-lg p-3">
                            {cleanLinkedRecordValue(String(value))}
                          </p>
                        );
                      }
                      if (field.type === 'date' && typeof value === 'string') {
                        return <span className="text-foreground">{formatDate(value)}</span>;
                      }
                      if (field.type === 'dateTime' && typeof value === 'string') {
                        return <span className="text-foreground">{formatDateTime(value)}</span>;
                      }
                      return formatDetailValue(value, field.name);
                    };
                    
                    return (
                      <div key={field.id || field.name} className="border-b border-border/50 pb-4 last:border-0">
                        <label className="text-sm font-bold text-emerald-500 tracking-wide block mb-2">
                          {field.name}
                        </label>
                        <div className="text-foreground">
                          {renderFieldValue()}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-12">
                No record data available
              </div>
            )}
          </div>

          <div className="border-t border-border pt-4 flex justify-end">
            <Button
              variant="outline"
              className="btn-secondary"
              onClick={() => setExpandedLinkedDialogOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={previewFileDialogOpen} onOpenChange={setPreviewFileDialogOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="border-b border-border p-4">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  {isImageFile(previewFile) ? (
                    <Image className="w-5 h-5 text-primary" />
                  ) : (
                    <File className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <span className="text-lg truncate block">{previewFile?.filename || 'File Preview'}</span>
                  {previewFile?.type && (
                    <p className="text-sm font-normal text-muted-foreground mt-1">
                      {previewFile.type}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={previewFile?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </a>
                <a
                  href={previewFile?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-black/20 flex items-center justify-center p-4 min-h-[300px]">
            {previewFile && isImageFile(previewFile) ? (
              <img
                src={previewFile.url}
                alt={previewFile.filename || 'Preview'}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
              />
            ) : previewFile?.type?.includes('pdf') ? (
              <iframe
                src={previewFile.url}
                title="PDF Preview"
                className="w-full h-[70vh] rounded-lg"
              />
            ) : previewFile?.type?.includes('video') ? (
              <video
                src={previewFile.url}
                controls
                className="max-w-full max-h-[70vh] rounded-lg"
              />
            ) : previewFile?.type?.includes('audio') ? (
              <div className="flex flex-col items-center gap-4 p-8">
                <File className="w-16 h-16 text-muted-foreground" />
                <audio src={previewFile.url} controls className="w-full max-w-md" />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 p-8 text-center">
                <File className="w-20 h-20 text-muted-foreground" />
                <div>
                  <p className="text-lg font-medium text-foreground mb-2">
                    {previewFile?.filename || 'File'}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    This file type cannot be previewed directly.
                  </p>
                  <a
                    href={previewFile?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download File
                  </a>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
